import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getImageUrl } from '../../utils/imageUtils';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardMedia,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  Receipt as ReceiptIcon,
  Clear as ClearIcon,
  Assignment as AssignmentIcon,
  MoneyOff as MoneyOffIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';

// No mock data - using database data only

// Status options
const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Payment status options
const paymentStatusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
];

interface OrderItem {
  id?: number;
  productId: number;
  productName?: string;
  sku?: string;
  quantity: number;
  fulfilledQuantity: number; // Added fulfilled quantity field
  unitPrice: number;
  discount: number;
  tax: number;
  total?: number;
  orderType: 'box' | 'single' | 'both'; // Added order type field
  // For 'both' type quantities
  boxQuantity?: number;
  singleQuantity?: number;
  fulfilledBoxQuantity?: number;
  fulfilledSingleQuantity?: number;
  // Stored product data snapshots for edit mode
  storedProductData?: {
    sku: string;
    name: string;
    unit_size: string;
    qty_in_box: number;
    unit_type: string;
  };
}

interface OrderFormData {
  customerId: number;
  orderDate: string;
  status: string;
  paymentStatus: string;
  shippingAddress: string;
  notes: string;
  items: OrderItem[];
}

const OrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading, hasPermission } = useAuth();
  const { formatCurrency } = useSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<OrderFormData>({
    customerId: 0,
    orderDate: new Date().toISOString().split('T')[0],
    status: 'pending',
    paymentStatus: 'draft',
    shippingAddress: '',
    notes: '',
    items: [],
  });

  // Autosave state management
  const [isDraftMode, setIsDraftMode] = useState(false);
  const [draftOrderId, setDraftOrderId] = useState<number | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [pendingAutosave, setPendingAutosave] = useState(false);

  const [customers, setCustomers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<any[]>(products);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | string>('all');
  const [loading, setLoading] = useState(true);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [discount, setDiscount] = useState(0);
  const [errors, setErrors] = useState<any>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoiceSuccess, setInvoiceSuccess] = useState<string | null>(null);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [existingInvoice, setExistingInvoice] = useState<any | null>(null);
  const [downloadingPickingList, setDownloadingPickingList] = useState(false);
  const [productOrderTypes, setProductOrderTypes] = useState<{[productId: number]: 'box' | 'single' | 'both'}>({});
  const [productBoxQuantities, setProductBoxQuantities] = useState<{[productId: number]: number}>({});
  const [productSingleQuantities, setProductSingleQuantities] = useState<{[productId: number]: number}>({});
  
  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Warning dialog state
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  
  // Refund states
  const [refunds, setRefunds] = useState<any[]>([]);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [editingRefund, setEditingRefund] = useState<any | null>(null);
  
  // Helper function to get stock limit based on order type
  const getStockLimit = (product: any, orderType: 'box' | 'single' | 'both') => {
    if (orderType === 'box') {
      return product.stockCount || 0;
    } else if (orderType === 'single') {
      return product.stockSingle || 0;
    }
    // For 'both' type, return an object with both limits
    return {
      boxStock: product.stockCount || 0,
      singleStock: product.stockSingle || 0
    };
  };
  
  // Image preview state
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<{url: string, name: string} | null>(null);

  // Store order data when editing
  const [orderData, setOrderData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch customers
        const customersResponse = await api.get('/customers');
        if (customersResponse.data && customersResponse.data.success) {
          setCustomers(customersResponse.data.data.map((customer: any) => ({
            id: customer.id,
            name: customer.company_name,
            email: customer.email,
            phone: customer.phone,
            address: `${customer.address}${customer.city ? `, ${customer.city}` : ''}${customer.state ? `, ${customer.state}` : ''}${customer.postal_code ? ` ${customer.postal_code}` : ''}${customer.country ? `, ${customer.country}` : ''}`
          })));
        }

        // If editing, fetch order details
        if (isEditMode && id) {
          const orderResponse = await api.get(`/orders/${id}`);
          if (orderResponse.data && orderResponse.data.success) {
            const order = orderResponse.data.data;
            setOrderData(order); // Store the order data
            
            // Format the date properly for the input field (YYYY-MM-DD)
            // Preserve the original date without timezone adjustments
            let formattedDate = order.order_date;
            if (formattedDate) {
              // Handle various date formats that might come from the database
              if (typeof formattedDate === 'string') {
                // Remove any time component and timezone info
                formattedDate = formattedDate.split('T')[0].split(' ')[0];
                
                // Ensure it's in YYYY-MM-DD format
                if (formattedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                  // Already in correct format, use as-is
                } else {
                  // Try to parse and reformat if it's in a different format
                  console.warn('Unexpected date format:', formattedDate);
                  formattedDate = new Date().toISOString().split('T')[0];
                }
              } else if (formattedDate instanceof Date) {
                // If it's a Date object, format it safely
                const year = formattedDate.getFullYear();
                const month = String(formattedDate.getMonth() + 1).padStart(2, '0');
                const day = String(formattedDate.getDate()).padStart(2, '0');
                formattedDate = `${year}-${month}-${day}`;
              } else {
                // Fallback for unexpected types
                formattedDate = new Date().toISOString().split('T')[0];
              }
            } else {
              formattedDate = new Date().toISOString().split('T')[0]; // Fallback to today
            }
            
            setFormData({
              customerId: order.customer_id,
              orderDate: formattedDate,
              status: order.status,
              paymentStatus: order.payment_status,
              shippingAddress: order.shipping_address || '',
              notes: order.notes || '',
              items: order.items.map((item: any) => ({
                id: item.id,
                productId: Number(item.product_id) || 0,
                productName: item.product_name,
                sku: item.sku,
                quantity: Number(item.quantity) || 0,
                fulfilledQuantity: item.fulfilled_quantity !== undefined && item.fulfilled_quantity !== null ? Number(item.fulfilled_quantity) : Number(item.quantity) || 0, // Properly handle fulfilled_quantity even when it's 0
                unitPrice: Number(item.unit_price) || 0,
                discount: Number(item.discount) || 0,
                tax: Number(item.tax_rate) || 0,
                total: Number(item.total) || 0,
                // Include stored product data snapshots
                storedProductData: {
                  sku: item.sku,
                  name: item.name || item.product_name,
                  unit_size: item.unit_size,
                  qty_in_box: item.qty_in_box,
                  unit_type: item.unit_type
                },
                orderType: item.order_type as 'box' | 'single' | 'both',
                boxQuantity: item.box_quantity || undefined,
                singleQuantity: item.single_quantity || undefined,
                fulfilledBoxQuantity: item.fulfilled_box_quantity || undefined,
                fulfilledSingleQuantity: item.fulfilled_single_quantity || undefined,
              }))
            });
            
            // Check if this is a draft order OR existing order being edited
            if (order.status === 'draft') {
              setIsDraftMode(true);
              setDraftOrderId(parseInt(id));
            } else {
              // For non-draft orders, track them for autosave
              setDraftOrderId(parseInt(id));
            }
          }
        }
        
        // Fetch categories
        const categoriesResponse = await api.get('/categories');
        if (categoriesResponse.data && categoriesResponse.data.success) {
          // Add an 'All Categories' option at the beginning
          const allCategories = [
            { id: 'all', name: 'All Categories' },
            ...categoriesResponse.data.data
          ];
          setCategories(allCategories);
        }
        
        // Fetch products
        const productsResponse = await api.get('/products');
        if (productsResponse.data && productsResponse.data.success) {
          const mappedProducts = productsResponse.data.data.map((product: any) => {
            return {
              id: product.id,
              name: product.name,
              sku: product.sku,
              image: product.image_url, // Use the correct field name from the database
              unit_size: product.unit_size || '',
              qtyInBox: product.qty_in_box || 0,
              unitType: product.unit_type || 'unit',
              unitPrice: Number(product.unit_price) || 0,
              tax: Number(product.tax_rate) || 0,
              taxRate: Number(product.tax_rate) || 0, // Add explicit taxRate field
              stockCount: Number(product.stock_quantity) || 0,
              stockSingle: Number(product.stock_single) || 0,
              category: {
                id: product.category_id,
                name: product.category_name || 'Uncategorized'
              }
            };
          });
          
          setProducts(mappedProducts);
          setFilteredProducts(mappedProducts);
          // We've already set filtered products above
        }
        
        // If in edit mode, fetch order data
        if (isEditMode && id) {
          await fetchOrderData();
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setSubmitError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && isAuthenticated) {
      fetchData();
    }
  }, [authLoading, isAuthenticated, id, isEditMode]);
  
  // Separate useEffect to check for existing invoice whenever the order ID changes
  useEffect(() => {
    if (!authLoading && isAuthenticated && isEditMode && id) {
      // Check if there's an invoice for this order
      checkExistingInvoice(id);
      // Fetch refunds for this order
      fetchRefunds();
    }
  }, [authLoading, isAuthenticated, id, isEditMode]);

  // Set selected customer when customers are loaded or order data changes
  useEffect(() => {
    if (isEditMode && orderData && customers.length > 0) {
      const customer = customers.find(c => c.id === orderData.customer_id);
      if (customer) {
        setSelectedCustomer(customer);
      }
    }
  }, [customers, orderData, isEditMode]);

  const handleCustomerChange = (event: any, newValue: any) => {
    setSelectedCustomer(newValue);
    let newFormData;
    if (newValue) {
      newFormData = {
        ...formData,
        customerId: newValue.id,
        shippingAddress: newValue.address,
      };
      setFormData(newFormData);
    } else {
      newFormData = {
        ...formData,
        customerId: 0,
        shippingAddress: '',
      };
      setFormData(newFormData);
    }
    
    // Trigger autosave if we have items and customer
    if (newFormData.items.length > 0 && newFormData.customerId > 0) {
      setHasUnsavedChanges(true);
      debouncedAutosave(newFormData);
    }
  };

  const handleImagePreview = (imageUrl: string, productName: string) => {
    setPreviewImage({ url: imageUrl, name: productName });
    setImagePreviewOpen(true);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewImage(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | { target: { name?: string; value: unknown } }) => {
    const { name, value } = e.target;
    if (name) {
      const newFormData = {
        ...formData,
        [name]: value,
      };
      setFormData(newFormData);
      
      // Trigger autosave if we have items and customer
      if (newFormData.items.length > 0 && newFormData.customerId > 0) {
        setHasUnsavedChanges(true);
        debouncedAutosave(newFormData);
      }
    }
  };

  // Handle category change for product filtering
  const handleCategoryChange = (event: React.SyntheticEvent, newValue: number | string) => {
    setSelectedCategory(newValue);
    
    // Filter products based on the selected category and current search term
    let filtered = products;
    
    if (newValue !== 'all') {
      filtered = filtered.filter(product => product.category.id === newValue);
    }
    
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredProducts(filtered);
  };

  const handleAddItem = () => {
    // Check if customer is selected before allowing product addition
    if (!selectedCustomer || formData.customerId === 0) {
      setWarningDialogOpen(true);
      return;
    }

    setProductDialogOpen(true);
    setSelectedProduct(null);
    setQuantity(1);
    setDiscount(0);
    setSearchTerm(''); // Reset search term when opening the dialog
    setSelectedCategory('all'); // Reset category filter when opening the dialog
    setFilteredProducts(products); // Reset filtered products to show all products
    setProductOrderTypes({}); // Reset order types for fresh selection
    setProductBoxQuantities({}); // Reset box quantities
    setProductSingleQuantities({}); // Reset single quantities
  };

  const handleProductSelect = (event: any, newValue: any) => {
    setSelectedProduct(newValue);
  };

  // This function is no longer used as we're adding products directly from the table
  const handleAddProductToOrder = () => {
    setProductDialogOpen(false);
  };

  const handleRemoveItem = async (index: number) => {
    const item = formData.items[index];

    // Check if we need to restore stock
    // Only restore if:
    // 1. Product exists in database (productId is valid)
    // 2. Item has quantities that were deducted from stock
    const shouldRestoreStock =
      item.productId &&
      item.productId !== 0 &&
      (
        (item.orderType === 'box' && (item.fulfilledBoxQuantity || 0) > 0) ||
        (item.orderType === 'single' && (item.fulfilledSingleQuantity || 0) > 0) ||
        (item.orderType === 'both' && ((item.fulfilledBoxQuantity || 0) > 0 || (item.fulfilledSingleQuantity || 0) > 0))
      );

    if (shouldRestoreStock) {
      try {
        // Restore stock before removing the item
        await restoreStockForItemDelete(
          item.productId!,
          item.orderType,
          item.fulfilledBoxQuantity || 0,
          item.fulfilledSingleQuantity || 0
        );
        console.log(`Stock restored for deleted item: Product ${item.productId}, Type: ${item.orderType}`);
      } catch (error) {
        console.error('Failed to restore stock for deleted item:', error);
        // Continue with deletion even if stock restoration fails
        // (product might have been deleted from database)
      }
    } else {
      console.log('No stock restoration needed for deleted item');
    }

    // Remove item from UI
    const updatedItems = [...formData.items];
    updatedItems.splice(index, 1);
    const newFormData = {
      ...formData,
      items: updatedItems,
    };
    setFormData(newFormData);
    setHasUnsavedChanges(true);
    debouncedAutosave(newFormData);
  };

  // Helper function to get box price for a product
  const getBoxPrice = (productId: number, unitPrice: number, storedProductData?: any) => {
    // Use stored product data if available (for existing order items), otherwise use current product data
    let qtyInBox = 1;
    if (storedProductData && storedProductData.qty_in_box) {
      qtyInBox = storedProductData.qty_in_box;
    } else {
      const product = products.find(p => p.id === productId);
      qtyInBox = product ? product.qtyInBox : 1;
    }
    return unitPrice * qtyInBox;
  };

  // Helper function to calculate item total based on order type
  const calculateItemTotal = (item: OrderItem, useOrderQty: boolean = false) => {
    // Use stored product data if available (for existing order items), otherwise use current product data
    let qtyInBox = 1;
    if (item.storedProductData && item.storedProductData.qty_in_box) {
      qtyInBox = item.storedProductData.qty_in_box;
    } else {
      const product = products.find(p => p.id === item.productId);
      qtyInBox = product ? product.qtyInBox : 1;
    }
    const quantity = useOrderQty ? item.quantity : item.fulfilledQuantity;
    const unitPrice = Number(item.unitPrice) || 0;
    const discount = Number(item.discount) || 0;
    const tax = Number(item.tax) || 0;

    let basePrice: number;
    
    switch (item.orderType) {
      case 'single':
        basePrice = unitPrice; // Single unit price
        break;
      case 'box':
        basePrice = unitPrice * qtyInBox; // Box price (unit price * qty in box)
        break;
      case 'both':
        // For 'both', we'll need to determine logic - for now, default to box
        basePrice = unitPrice * qtyInBox;
        break;
      default:
        basePrice = unitPrice * qtyInBox; // Default to box
    }

    const subtotal = quantity * basePrice * (1 - discount / 100);
    const totalWithTax = subtotal * (1 + tax / 100);
    
    return totalWithTax;
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => {
      const quantity = Number(item.fulfilledQuantity) || 0;
      const unitPrice = Number(item.unitPrice) || 0;
      const discount = Number(item.discount) || 0;
      
      let basePrice: number;
      // Use stored product data if available (for existing order items), otherwise use current product data
      let qtyInBox = 1;
      if (item.storedProductData && item.storedProductData.qty_in_box) {
        qtyInBox = item.storedProductData.qty_in_box;
      } else {
        const product = products.find(p => p.id === item.productId);
        qtyInBox = product ? product.qtyInBox : 1;
      }
      
      switch (item.orderType) {
        case 'single':
          basePrice = unitPrice;
          return sum + (quantity * basePrice * (1 - discount / 100));
        case 'box':
          basePrice = unitPrice * qtyInBox;
          return sum + (quantity * basePrice * (1 - discount / 100));
        case 'both':
          // For 'both' type, calculate separately for boxes and singles
          const boxQty = item.fulfilledBoxQuantity || 0;
          const singleQty = item.fulfilledSingleQuantity || 0;
          const boxPrice = unitPrice * qtyInBox;
          const singlePrice = unitPrice;
          const boxSubtotal = boxQty * boxPrice * (1 - discount / 100);
          const singleSubtotal = singleQty * singlePrice * (1 - discount / 100);
          return sum + boxSubtotal + singleSubtotal;
        default:
          // Default to single pricing if order type is not properly set
          basePrice = unitPrice;
          return sum + (quantity * basePrice * (1 - discount / 100));
      }
    }, 0);
  };

  const calculateTax = () => {
    return formData.items.reduce((sum, item) => {
      const quantity = Number(item.fulfilledQuantity) || 0;
      const unitPrice = Number(item.unitPrice) || 0;
      const discount = Number(item.discount) || 0;
      const tax = Number(item.tax) || 0;
      
      let basePrice: number;
      // Use stored product data if available (for existing order items), otherwise use current product data
      let qtyInBox = 1;
      if (item.storedProductData && item.storedProductData.qty_in_box) {
        qtyInBox = item.storedProductData.qty_in_box;
      } else {
        const product = products.find(p => p.id === item.productId);
        qtyInBox = product ? product.qtyInBox : 1;
      }
      
      switch (item.orderType) {
        case 'single':
          basePrice = unitPrice;
          return sum + (quantity * basePrice * (1 - discount / 100) * (tax / 100));
        case 'box':
          basePrice = unitPrice * qtyInBox;
          return sum + (quantity * basePrice * (1 - discount / 100) * (tax / 100));
        case 'both':
          // For 'both' type, calculate tax separately for boxes and singles
          const boxQty = item.fulfilledBoxQuantity || 0;
          const singleQty = item.fulfilledSingleQuantity || 0;
          const boxPrice = unitPrice * qtyInBox;
          const singlePrice = unitPrice;
          const boxSubtotal = boxQty * boxPrice * (1 - discount / 100);
          const singleSubtotal = singleQty * singlePrice * (1 - discount / 100);
          const totalTax = (boxSubtotal + singleSubtotal) * (tax / 100);
          return sum + totalTax;
        default:
          // Default to single pricing if order type is not properly set
          basePrice = unitPrice;
          return sum + (quantity * basePrice * (1 - discount / 100) * (tax / 100));
      }
    }, 0);
  };

  const calculateRefundTotal = () => {
    return refunds.reduce((sum, refund) => sum + parseFloat(refund.refund_amount || 0), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() - calculateRefundTotal();
  };

  // Helper function to check if product details have changed
  const hasProductChanged = (item: OrderItem) => {
    // Skip check for deleted products or new items without snapshot
    if (!item.productId || item.productId === 0 || !item.storedProductData) {
      return false;
    }

    // Find current product data
    const currentProduct = products.find(p => p.id === item.productId);
    if (!currentProduct) {
      return false; // Product not found in current products list
    }

    // Compare snapshot with current data
    const snapshot = item.storedProductData;
    return (
      snapshot.name !== currentProduct.name ||
      snapshot.unit_size !== currentProduct.unit_size ||
      snapshot.qty_in_box !== currentProduct.qtyInBox ||
      snapshot.unit_type !== currentProduct.unitType
    );
  };

  // Helper function to get product change details for tooltip
  const getProductChangeDetails = (item: OrderItem) => {
    if (!item.productId || !item.storedProductData) return '';

    const currentProduct = products.find(p => p.id === item.productId);
    if (!currentProduct) return '';

    const snapshot = item.storedProductData;
    const changes: string[] = [];

    if (snapshot.name !== currentProduct.name) {
      changes.push(`Name: "${snapshot.name}" → "${currentProduct.name}"`);
    }
    if (snapshot.unit_size !== currentProduct.unit_size) {
      changes.push(`Size: ${snapshot.unit_size} → ${currentProduct.unit_size}`);
    }
    if (snapshot.qty_in_box !== currentProduct.qtyInBox) {
      changes.push(`Qty/Box: ${snapshot.qty_in_box} → ${currentProduct.qtyInBox}`);
    }
    if (snapshot.unit_type !== currentProduct.unitType) {
      changes.push(`Type: ${snapshot.unit_type} → ${currentProduct.unitType}`);
    }

    return changes.join('\n');
  };

  // Real-time stock functions
  const getCurrentStock = async (productId: number) => {
    try {
      const response = await api.get(`/orders/products/${productId}/current-stock`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch current stock:', error);
      return { boxStock: 0, singleStock: 0 };
    }
  };

  // Check if quantity increase is allowed
  const canIncreaseQuantity = async (productId: number, currentQty: number, type: 'box' | 'single') => {
    const currentStock = await getCurrentStock(productId);
    const maxStock = type === 'box' ? currentStock.boxStock : currentStock.singleStock;
    return currentQty < maxStock;
  };

  // Get maximum allowed quantity
  const getMaxQuantity = async (productId: number, type: 'box' | 'single') => {
    const currentStock = await getCurrentStock(productId);
    return type === 'box' ? currentStock.boxStock : currentStock.singleStock;
  };

  // Helper function to get maximum allowed quantity for existing orders
  const getMaxAllowedQuantity = (productId: number, type: 'box' | 'single') => {
    const product = products.find(p => p.id === productId);
    if (!product) return 0;

    const currentAvailableStock = type === 'box' ? (product.stockCount || 0) : (product.stockSingle || 0);
    
    // For existing orders, add the originally ordered quantity to available stock
    if (isEditMode && orderData?.items) {
      const originalItem = orderData.items.find((item: any) => item.product_id === productId);
      if (originalItem) {
        const originalQuantity = type === 'box' 
          ? (originalItem.box_quantity || 0)
          : (originalItem.single_quantity || 0);
        return currentAvailableStock + originalQuantity;
      }
    }
    
    return currentAvailableStock;
  };

  // Enhanced debounce utility with request deduplication
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    let lastArgs: any[] | null = null;
    
    return (...args: any[]) => {
      lastArgs = args;
      setPendingAutosave(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (lastArgs && !isAutoSaving) {
          func.apply(null, lastArgs);
          lastArgs = null;
          setPendingAutosave(false);
        }
      }, delay);
    };
  };

  // Enhanced autosave function with race condition protection
  const autosaveOrder = useCallback(async (formData: OrderFormData) => {
    if (!formData.items.length || formData.customerId === 0) return;
    
    // Prevent multiple simultaneous autosave requests
    if (isAutoSaving) {
      return;
    }
    
    try {
      setIsAutoSaving(true);
      setPendingAutosave(false); // Clear pending flag as we're now processing

      const orderData = {
        ...formData,
        items: formData.items.map(item => ({
          ...item,
          id: item.id ? Number(item.id) : undefined, // Ensure ID is number or undefined
          productId: Number(item.productId) || 0,
          quantity: Number(item.quantity) || 0,
          fulfilledQuantity: Number(item.fulfilledQuantity) || 0,
          unitPrice: Number(item.unitPrice) || 0,
          discount: Number(item.discount) || 0,
          tax: Number(item.tax) || 0
        })),
        // Preserve existing status for edit mode, use draft for new orders
        status: (isEditMode || isDraftMode) ? formData.status : 'draft',
        paymentStatus: (isEditMode || isDraftMode) ? formData.paymentStatus : 'draft',
        isAutosave: true,
        updateStock: true
      };

      let response;
      if (isEditMode && id) {
        // Update existing order (preserves status)
        response = await api.put(`/orders/${id}`, orderData);
      } else if (isDraftMode && draftOrderId) {
        // Update existing draft
        response = await api.put(`/orders/${draftOrderId}`, orderData);
      } else {
        // Create new draft
        response = await api.post('/orders', orderData);
        if (response.data.success) {
          setDraftOrderId(response.data.data.id);
          setIsDraftMode(true);
        }
      }
      
      if (response.data.success) {
        setLastSaved(new Date());
        setHasUnsavedChanges(false);

        // Reload order data to get updated item IDs after save
        // This prevents stale ID issues when items are deleted and re-inserted
        if ((isEditMode && id) || (isDraftMode && draftOrderId)) {
          try {
            const orderId = isEditMode ? id : draftOrderId;
            const orderResponse = await api.get(`/orders/${orderId}`);
            if (orderResponse.data.success) {
              const order = orderResponse.data.data;

              // Update formData with fresh item IDs from database
              setFormData(prev => ({
                ...prev,
                items: order.items.map((item: any) => ({
                  id: item.id, // Fresh ID from database
                  productId: Number(item.product_id) || 0,
                  productName: item.product_name,
                  sku: item.sku,
                  quantity: Number(item.quantity) || 0,
                  fulfilledQuantity: item.fulfilled_quantity !== undefined && item.fulfilled_quantity !== null ? Number(item.fulfilled_quantity) : Number(item.quantity) || 0,
                  unitPrice: Number(item.unit_price) || 0,
                  discount: Number(item.discount) || 0,
                  tax: Number(item.tax_rate) || 0,
                  total: Number(item.total) || 0,
                  storedProductData: {
                    sku: item.sku,
                    name: item.name || item.product_name,
                    unit_size: item.unit_size,
                    qty_in_box: item.qty_in_box,
                    unit_type: item.unit_type
                  },
                  orderType: item.order_type as 'box' | 'single' | 'both',
                  boxQuantity: item.box_quantity || undefined,
                  singleQuantity: item.single_quantity || undefined,
                  fulfilledBoxQuantity: item.fulfilled_box_quantity || undefined,
                  fulfilledSingleQuantity: item.fulfilled_single_quantity || undefined,
                }))
              }));
            }
          } catch (reloadError) {
            console.error('Error reloading order data after autosave:', reloadError);
            // Don't fail autosave if reload fails
          }
        }
      }

    } catch (error: any) {
      console.error('Autosave failed:', error);
      if (error.response?.status === 400) {
        // Stock validation failed - show error but don't break UI
        setSnackbar({
          open: true,
          message: error.response.data.error,
          severity: 'error'
        });
      }
    } finally {
      setIsAutoSaving(false);
    }
  }, [isDraftMode, draftOrderId, formData.customerId, isAutoSaving]);

  // Debounced autosave
  const debouncedAutosave = useMemo(
    () => debounce(autosaveOrder, 500),
    [autosaveOrder]
  );

  // Helper function to restore stock when changing order types
  const restoreStockForOrderTypeChange = async (
    productId: number,
    oldOrderType: string,
    oldQuantity: number,
    oldBoxQuantity?: number,
    oldSingleQuantity?: number
  ) => {
    try {
      await api.post('/orders/restore-stock-order-type-change', {
        productId,
        oldOrderType,
        oldQuantity,
        oldBoxQuantity,
        oldSingleQuantity
      });
    } catch (error) {
      console.error('Error restoring stock for order type change:', error);
      // Show error notification but don't block the UI
      setSnackbar({
        open: true,
        message: 'Warning: Stock restoration failed. Please check stock levels manually.',
        severity: 'warning'
      });
    }
  };

  // Helper function to restore stock when deleting an order item
  const restoreStockForItemDelete = async (
    productId: number,
    orderType: string,
    boxQuantity: number,
    singleQuantity: number
  ) => {
    try {
      const response = await api.post('/orders/restore-stock-item-delete', {
        productId,
        orderType,
        boxQuantity,
        singleQuantity
      });

      // Log success for debugging
      if (response.data.success) {
        if (response.data.skipped) {
          console.log('Stock restoration skipped:', response.data.message);
        } else {
          console.log('Stock restored successfully:', response.data.data);
        }
      }

      return response.data;
    } catch (error) {
      console.error('Error restoring stock for item delete:', error);
      // Don't show error to user - product might have been deleted from database
      // which is an expected scenario
      return { success: false, error };
    }
  };

  // Function to generate or update invoice from order (includes refund sync)
  const handleGenerateInvoice = async () => {
    if (!id) return;
    
    setGeneratingInvoice(true);
    setInvoiceSuccess(null);
    setInvoiceError(null);
    
    try {
      let response: { data: { success: boolean; data: { id: number; invoice_number?: string } } };
      let invoiceId: number;
      let invoiceNumber: string;
      
      if (existingInvoice) {
        // Update existing invoice
        response = await api.post(`/invoices/update-from-order/${id}`);
        invoiceId = existingInvoice.id;
        invoiceNumber = existingInvoice.invoice_number;
        
        if (response.data.success) {
          setInvoiceSuccess(`Invoice updated successfully (items and refunds synced)`);
          
          // Refresh order data and invoice data
          await fetchOrderData();
          await fetchRefunds();
          
          // Navigate to the invoice detail page after a short delay
          setTimeout(() => {
            navigate(`/invoices/${invoiceId}`);
          }, 1500);
        }
      } else {
        // Create new invoice
        // Calculate due date (30 days from now by default)
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 30);
        const dueDateStr = dueDate.toISOString().split('T')[0];
        
        response = await api.post('/invoices', {
          orderId: id,
          issueDate: new Date().toISOString().split('T')[0],
          dueDate: dueDateStr,
          status: 'unpaid',
          notes: `Invoice generated from order ${orderData?.order_number || id}`,
          updateOrderStatus: true
        });
        
        if (response.data.success) {
          invoiceId = response.data.data.id;
          invoiceNumber = response.data.data.invoice_number || 'New Invoice';
          
          // After successful invoice creation, refunds are automatically included via backend
          // But we can still sync to ensure everything is up to date
          try {
            await api.put(`/orders/${id}/sync-invoice`);
            setInvoiceSuccess(`Invoice ${invoiceNumber} generated successfully (including refunds)`);
          } catch (refundError) {
            console.error('Error syncing refunds:', refundError);
            setInvoiceSuccess(`Invoice ${invoiceNumber} generated successfully (refunds sync may have failed)`);
          }
          
          // Refresh order data to reflect updated payment status
          await fetchOrderData();
          await fetchRefunds();
          
          // Navigate to the invoice detail page after a short delay
          setTimeout(() => {
            navigate(`/invoices/${invoiceId}`);
          }, 1500);
        }
      }
    } catch (error: any) {
      console.error('Error with invoice operation:', error);
      setInvoiceError(error.response?.data?.message || 'Failed to process invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  };
  
  // Function to view existing invoice
  const handleViewInvoice = () => {
    if (existingInvoice && existingInvoice.id) {
      navigate(`/invoices/${existingInvoice.id}`);
    }
  };
  
  // Function to check if an invoice exists for this order
  const checkExistingInvoice = async (orderId: string) => {
    try {
      const response = await api.get(`/invoices/order/${orderId}`);
      
      if (response.data.success) {
        setExistingInvoice(response.data.data);
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error checking for existing invoice:', error);
      return null;
    }
  };

  // Refund management functions
  const fetchRefunds = async () => {
    if (!id) return;
    
    try {
      const response = await api.get(`/orders/${id}/refunds`);
      if (response.data.success) {
        setRefunds(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching refunds:', error);
    }
  };

  const handleAddRefund = () => {
    setEditingRefund(null);
    setRefundAmount('');
    setRefundReason('');
    setRefundDialogOpen(true);
  };

  const handleEditRefund = (refund: any) => {
    setEditingRefund(refund);
    setRefundAmount(refund.refund_amount.toString());
    setRefundReason(refund.reason || '');
    setRefundDialogOpen(true);
  };

  const handleSaveRefund = async () => {
    if (!refundAmount || parseFloat(refundAmount) <= 0) {
      alert('Please enter a valid refund amount');
      return;
    }

    try {
      const refundData = {
        refundAmount: parseFloat(refundAmount),
        reason: refundReason.trim() || null
      };

      if (editingRefund) {
        // Update existing refund
        await api.put(`/orders/${id}/refunds/${editingRefund.id}`, refundData);
      } else {
        // Add new refund
        await api.post(`/orders/${id}/refunds`, refundData);
      }

      setRefundDialogOpen(false);
      setRefundAmount('');
      setRefundReason('');
      setEditingRefund(null);
      await fetchRefunds();
    } catch (error) {
      console.error('Error saving refund:', error);
      alert('Error saving refund. Please try again.');
    }
  };

  const handleDeleteRefund = async (refundId: number) => {
    if (!window.confirm('Are you sure you want to delete this refund?')) return;

    try {
      await api.delete(`/orders/${id}/refunds/${refundId}`);
      await fetchRefunds();
    } catch (error) {
      console.error('Error deleting refund:', error);
      alert('Error deleting refund. Please try again.');
    }
  };


  // Function to download picking list PDF
  const handleDownloadPickingList = async () => {
    if (!id) return;
    
    setDownloadingPickingList(true);
    
    try {
      const response = await api.get(`/orders/${id}/picking-list-pdf`, {
        responseType: 'blob'
      });
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create filename with customer name and order number
      const customerName = (orderData?.customer_name || 'Unknown').replace(/[^a-zA-Z0-9\-_]/g, '-');
      link.download = `picking-list-${customerName}-${orderData?.order_number || id}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      console.error('Error downloading picking list:', error);
      // You could add error handling here if needed
    } finally {
      setDownloadingPickingList(false);
    }
  };

  // Function to fetch order data
  const fetchOrderData = async () => {
    if (!id) return;
    
    try {
      const response = await api.get(`/orders/${id}`);
      if (response.data.success) {
        setOrderData(response.data.data);
        // Check if there's an invoice for this order
        await checkExistingInvoice(id);
      }
    } catch (error) {
      console.error('Error fetching order data:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};

    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }

    if (!formData.orderDate) {
      newErrors.orderDate = 'Order date is required';
    }

    if (!formData.shippingAddress.trim()) {
      newErrors.shippingAddress = 'Shipping address is required';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let response;
      
      // Prepare the data with proper type conversions
      // Ensure we preserve the exact date string to prevent timezone issues
      const orderData = {
        ...formData,
        customerId: Number(formData.customerId),
        // Keep the orderDate exactly as it is in the form without any date conversion
        orderDate: formData.orderDate,
        items: formData.items.map(item => ({
          ...item,
          id: item.id ? Number(item.id) : undefined, // Ensure ID is number or undefined
          productId: Number(item.productId),
          quantity: Number(item.quantity),
          fulfilledQuantity: Number(item.fulfilledQuantity),
          unitPrice: Number(item.unitPrice),
          discount: Number(item.discount),
          tax: Number(item.tax)
        })),
        // Convert from draft to final order
        status: formData.status === 'draft' ? 'pending' : formData.status,
        paymentStatus: formData.paymentStatus === 'draft' ? 'unpaid' : formData.paymentStatus,
        isAutosave: false, // This is final submission
        updateStock: true
      };

      if (isEditMode && id) {
        // Update existing order
        response = await api.put(`/orders/${id}`, orderData);
      } else if (isDraftMode && draftOrderId) {
        // Convert draft to final order
        response = await api.put(`/orders/${draftOrderId}`, orderData);
      } else {
        // Create new final order
        response = await api.post('/orders', {
          ...orderData,
          generateInvoice: false // Do not generate invoice automatically
        });
      }
      
      if (response.data && response.data.success) {
        navigate('/orders');
      } else {
        setSubmitError('Failed to save order. Please try again.');
      }
    } catch (error: any) {
      console.error('Error saving order:', error);
      setSubmitError(error.response?.data?.message || 'Failed to save order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/orders')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {isEditMode ? 'Edit Order' : 'Create New Order'}
        </Typography>
        
        {/* Autosave status indicators */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
          {isDraftMode && (
            <Chip 
              label="Draft" 
              color="warning" 
              size="small" 
            />
          )}
          
          {isAutoSaving && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Saving...
              </Typography>
            </Box>
          )}
          
          {lastSaved && !isAutoSaving && (
            <Typography variant="caption" color="text.secondary">
              Last saved: {lastSaved.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
        
        {/* Action buttons - only show in edit mode */}
        {isEditMode && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Picking List Button */}
            <Button
              variant="outlined"
              color="info"
              onClick={handleDownloadPickingList}
              disabled={downloadingPickingList}
              startIcon={<AssignmentIcon />}
            >
              {downloadingPickingList ? 'Generating...' : 'Picking List'}
            </Button>
            
            {/* Invoice Buttons */}
            {existingInvoice ? (
              <>
                {hasPermission('invoices.read') && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleViewInvoice}
                  startIcon={<ReceiptIcon />}
                >
                  View Invoice
                </Button>
                )}
                {hasPermission('invoices.update') && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleGenerateInvoice}
                  disabled={generatingInvoice}
                  startIcon={<ReceiptIcon />}
                >
                  {generatingInvoice ? 'Updating...' : 'Update Invoice'}
                </Button>
                )}
              </>
            ) : (
              hasPermission('invoices.create') && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleGenerateInvoice}
                disabled={generatingInvoice}
                startIcon={<ReceiptIcon />}
              >
                {generatingInvoice ? 'Generating...' : 'Generate Invoice'}
              </Button>
              )
            )}
          </Box>
        )}
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}
      
      {/* Invoice generation messages */}
      {invoiceSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {invoiceSuccess}
        </Alert>
      )}
      
      {invoiceError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {invoiceError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {/* Order Details */}
          <Box sx={{ flex: '1 1 60%', minWidth: '300px' }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Order Details
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => option.name}
                    value={selectedCustomer}
                    onChange={handleCustomerChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer"
                        error={!!errors.customerId}
                        helperText={errors.customerId}
                        fullWidth
                      />
                    )}
                  />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                  <TextField
                    fullWidth
                    label="Order Date"
                    name="orderDate"
                    type="date"
                    value={formData.orderDate}
                    onChange={handleChange}
                    error={!!errors.orderDate}
                    helperText={errors.orderDate}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      label="Status"
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Status</InputLabel>
                    <Select
                      name="paymentStatus"
                      value={formData.paymentStatus}
                      onChange={handleChange}
                      label="Payment Status"
                    >
                      {paymentStatusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    label="Shipping Address"
                    name="shippingAddress"
                    value={formData.shippingAddress}
                    onChange={handleChange}
                    error={!!errors.shippingAddress}
                    helperText={errors.shippingAddress}
                    multiline
                    rows={2}
                  />
                </Box>
                <Box sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    label="Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    multiline
                    rows={2}
                  />
                </Box>
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Order Items
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<MoneyOffIcon />}
                    onClick={handleAddRefund}
                    color="error"
                  >
                    Add Refund
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                  >
                    Add Item
                  </Button>
                </Box>
              </Box>

              {errors.items && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.items}
                </Alert>
              )}

              <TableContainer>
                <Table size={isTablet ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      {!isMobile && <TableCell align="center">Type</TableCell>}
                      {!isMobile && <TableCell align="right">Ordered Qty</TableCell>}
                      {isEditMode && !isMobile && (
                        <TableCell align="right">Fulfilled Qty</TableCell>
                      )}
                      {!isMobile && <TableCell align="right">Unit Price</TableCell>}
                      {!isTablet && <TableCell align="right">Price</TableCell>}
                      {!isTablet && <TableCell align="right">Discount</TableCell>}
                      {!isTablet && <TableCell align="right">Tax</TableCell>}
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.items.map((item, index) => {
                      // Check if product is deleted (productId is null or 0)
                      const isProductDeleted = !item.productId || item.productId === 0;

                      return (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{item.productName}</Typography>
                          <Typography variant="body2" color="text.secondary">{item.sku}</Typography>
                          {(() => {
                            // Use stored product data if available (for existing order items), otherwise use current product data
                            if (item.storedProductData) {
                              return (
                                <Typography variant="body2" color="text.secondary">
                                  {item.storedProductData.unit_size} x {item.storedProductData.qty_in_box} ({item.storedProductData.unit_type})
                                </Typography>
                              );
                            } else {
                              // For new items, find the current product data
                              const product = products.find(p => p.id === item.productId);
                              if (product) {
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    {product.unit_size} x {product.qtyInBox} ({product.unitType})
                                  </Typography>
                                );
                              }
                            }
                            return null;
                          })()}
                          {isProductDeleted && (
                            <Chip
                              label="Deleted Product"
                              size="small"
                              color="error"
                              sx={{ height: '20px', fontSize: '0.7rem', mt: 0.5 }}
                            />
                          )}
                          {!isProductDeleted && hasProductChanged(item) && (
                            <Tooltip
                              title={
                                <Box sx={{ whiteSpace: 'pre-line' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                    Product details have changed:
                                  </Typography>
                                  <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                    {getProductChangeDetails(item)}
                                  </Typography>
                                </Box>
                              }
                              arrow
                              placement="right"
                            >
                              <Chip
                                label="Product Updated"
                                size="small"
                                color="warning"
                                sx={{ height: '20px', fontSize: '0.7rem', mt: 0.5 }}
                              />
                            </Tooltip>
                          )}
                          {/* Show quantity info on mobile */}
                          {isMobile && (
                            <Box sx={{ mt: 1 }}>
                              {/* Order Type Selector for Mobile */}
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '35px' }}>
                                  Type:
                                </Typography>
                                <Select
                                  size="small"
                                  value={item.orderType || 'box'}
                                  disabled={isProductDeleted}
                                  onChange={async (e) => {
                                    const newOrderType = e.target.value as 'box' | 'single' | 'both';
                                    const product = products.find(p => p.id === item.productId);
                                    const oldOrderType = item.orderType || 'box';

                                    // DISABLED: Stock restoration is now handled automatically by the backend
                                    // when it detects order type changes during autosave/update
                                    //
                                    // if (oldOrderType !== newOrderType && (item.quantity > 0 || (item.boxQuantity || 0) > 0 || (item.singleQuantity || 0) > 0)) {
                                    //   await restoreStockForOrderTypeChange(
                                    //     item.productId,
                                    //     oldOrderType,
                                    //     item.quantity,
                                    //     item.boxQuantity,
                                    //     item.singleQuantity
                                    //   );
                                    // }

                                    const updatedItems = formData.items.map((i, idx) => {
                                      if (idx === index) {
                                        // According to requirements: When switching order types,
                                        // stock is restored and quantities are reset to zero
                                        let newQuantity = 0;
                                        let newFulfilledQuantity = 0;
                                        let newBoxQuantity: number | undefined;
                                        let newSingleQuantity: number | undefined;
                                        let newFulfilledBoxQuantity: number | undefined;
                                        let newFulfilledSingleQuantity: number | undefined;
                                        
                                        if (newOrderType === 'both') {
                                          // Switching TO 'both' type - set both quantities to 0
                                          newBoxQuantity = 0;
                                          newSingleQuantity = 0;
                                          newFulfilledBoxQuantity = 0;
                                          newFulfilledSingleQuantity = 0;
                                          newQuantity = 0;
                                          newFulfilledQuantity = 0;
                                        } else {
                                          // Switching TO 'box' or 'single' type - set main quantity to 0
                                          newQuantity = 0;
                                          newFulfilledQuantity = 0;
                                          // Clear box/single quantities for non-'both' types
                                          newBoxQuantity = undefined;
                                          newSingleQuantity = undefined;
                                          newFulfilledBoxQuantity = undefined;
                                          newFulfilledSingleQuantity = undefined;
                                        }
                                        
                                        return {
                                          ...i, 
                                          orderType: newOrderType,
                                          quantity: newQuantity,
                                          fulfilledQuantity: newFulfilledQuantity,
                                          boxQuantity: newBoxQuantity,
                                          singleQuantity: newSingleQuantity,
                                          fulfilledBoxQuantity: newFulfilledBoxQuantity,
                                          fulfilledSingleQuantity: newFulfilledSingleQuantity
                                        };
                                      }
                                      return i;
                                    });
                                    
                                    const newFormData = { ...formData, items: updatedItems };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                  }}
                                  sx={{ minWidth: 80, '& .MuiSelect-select': { py: 0.5 } }}
                                >
                                  <MenuItem value="box">Box</MenuItem>
                                  <MenuItem value="single">Single</MenuItem>
                                  <MenuItem value="both">Both</MenuItem>
                                </Select>
                              </Box>
                              
                              {/* Quantity Display */}
                              {item.orderType === 'both' ? (
                                <>
                                  <Typography variant="body2" color="text.secondary">
                                    Box: {item.boxQuantity || 0} | Single: {item.singleQuantity || 0}
                                  </Typography>
                                  {isEditMode && (
                                    <Typography variant="body2" color="text.secondary">
                                      Fulfilled - Box: {item.fulfilledBoxQuantity || 0} | Single: {item.fulfilledSingleQuantity || 0}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Qty: {item.quantity} {isEditMode && `| Fulfilled: ${item.fulfilledQuantity}`}
                                </Typography>
                              )}
                              <Typography variant="body2" color="text.secondary">
                                Unit: {formatCurrency(item.unitPrice)}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        {!isMobile && (
                          <TableCell align="center">
                            <Select
                              size="small"
                              value={item.orderType || 'box'}
                              disabled={isProductDeleted}
                              onChange={async (e) => {
                                const newOrderType = e.target.value as 'box' | 'single' | 'both';
                                const product = products.find(p => p.id === item.productId);
                                const oldOrderType = item.orderType || 'box';

                                // DISABLED: Stock restoration is now handled automatically by the backend
                                // when it detects order type changes during autosave/update
                                //
                                // if (oldOrderType !== newOrderType && (item.quantity > 0 || (item.boxQuantity || 0) > 0 || (item.singleQuantity || 0) > 0)) {
                                //   await restoreStockForOrderTypeChange(
                                //     item.productId,
                                //     oldOrderType,
                                //     item.quantity,
                                //     item.boxQuantity,
                                //     item.singleQuantity
                                //   );
                                // }

                                const updatedItems = formData.items.map((i, idx) => {
                                  if (idx === index) {
                                    // According to requirements: When switching order types,
                                    // stock is restored and quantities are reset to zero
                                    let newQuantity = 0;
                                    let newFulfilledQuantity = 0;
                                    let newBoxQuantity: number | undefined;
                                    let newSingleQuantity: number | undefined;
                                    let newFulfilledBoxQuantity: number | undefined;
                                    let newFulfilledSingleQuantity: number | undefined;
                                    
                                    if (newOrderType === 'both') {
                                      // Switching TO 'both' type - set both quantities to 0
                                      newBoxQuantity = 0;
                                      newSingleQuantity = 0;
                                      newFulfilledBoxQuantity = 0;
                                      newFulfilledSingleQuantity = 0;
                                      newQuantity = 0;
                                      newFulfilledQuantity = 0;
                                    } else {
                                      // Switching TO 'box' or 'single' type - set main quantity to 0
                                      newQuantity = 0;
                                      newFulfilledQuantity = 0;
                                      // Clear box/single quantities for non-'both' types
                                      newBoxQuantity = undefined;
                                      newSingleQuantity = undefined;
                                      newFulfilledBoxQuantity = undefined;
                                      newFulfilledSingleQuantity = undefined;
                                    }
                                    
                                    return {
                                      ...i, 
                                      orderType: newOrderType,
                                      quantity: newQuantity,
                                      fulfilledQuantity: newFulfilledQuantity,
                                      boxQuantity: newBoxQuantity,
                                      singleQuantity: newSingleQuantity,
                                      fulfilledBoxQuantity: newFulfilledBoxQuantity,
                                      fulfilledSingleQuantity: newFulfilledSingleQuantity
                                    };
                                  }
                                  return i;
                                });
                                
                                const newFormData = { ...formData, items: updatedItems };
                                setFormData(newFormData);
                                setHasUnsavedChanges(true);
                                debouncedAutosave(newFormData);
                              }}
                              sx={{ minWidth: 80 }}
                            >
                              <MenuItem value="box">Box</MenuItem>
                              <MenuItem value="single">Single</MenuItem>
                              <MenuItem value="both">Both</MenuItem>
                            </Select>
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell align="right">
                            {item.orderType === 'both' ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ minWidth: '40px' }}>Box:</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton 
                                      color="error" 
                                      size="small"
                                      disabled={isProductDeleted || (item.boxQuantity || 0) === 0}
                                      onClick={() => {
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                boxQuantity: Math.max(0, (i.boxQuantity || 0) - 1),
                                                quantity: Math.max(0, (i.boxQuantity || 0) - 1) + (i.singleQuantity || 0),
                                                fulfilledQuantity: !isEditMode ? Math.max(0, (i.boxQuantity || 0) - 1) + (i.singleQuantity || 0) : i.fulfilledQuantity,
                                                fulfilledBoxQuantity: !isEditMode ? Math.max(0, (i.boxQuantity || 0) - 1) : (i.fulfilledBoxQuantity || 0),
                                                fulfilledSingleQuantity: !isEditMode ? (i.singleQuantity || 0) : (i.fulfilledSingleQuantity || 0)
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                    >
                                      <RemoveCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={item.boxQuantity || 0}
                                      onChange={(e) => {
                                        const newValue = parseInt(e.target.value, 10);
                                        if (isNaN(newValue) || newValue < 0) return;
                                        
                                        const maxBoxStock = getMaxAllowedQuantity(item.productId, 'box');
                                        const validBoxQty = Math.min(newValue, maxBoxStock);
                                        
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                boxQuantity: validBoxQty,
                                                quantity: validBoxQty + (i.singleQuantity || 0),
                                                fulfilledQuantity: !isEditMode ? validBoxQty + (i.singleQuantity || 0) : i.fulfilledQuantity,
                                                fulfilledBoxQuantity: !isEditMode ? validBoxQty : (i.fulfilledBoxQuantity || 0),
                                                fulfilledSingleQuantity: !isEditMode ? (i.singleQuantity || 0) : (i.fulfilledSingleQuantity || 0)
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                      inputProps={{
                                        min: 0,
                                        max: getMaxAllowedQuantity(item.productId, 'box'),
                                        style: { textAlign: 'center' }
                                      }}
                                      sx={{ width: '60px', mx: 1 }}
                                      disabled={isProductDeleted}
                                      onFocus={(e) => e.target.select()}
                                    />
                                    <IconButton 
                                      color="primary" 
                                      size="small"
                                      disabled={isProductDeleted || (() => {
                                        const maxAllowed = getMaxAllowedQuantity(item.productId, 'box');
                                        return (item.boxQuantity || 0) >= maxAllowed;
                                      })()}
                                      onClick={async () => {
                                        const currentBoxQty = item.boxQuantity || 0;
                                        
                                        // Check if increase is allowed using real-time stock
                                        if (!(await canIncreaseQuantity(item.productId, currentBoxQty, 'box'))) {
                                          const currentStock = await getCurrentStock(item.productId);
                                          setSnackbar({
                                            open: true,
                                            message: `Only ${currentStock.boxStock} boxes available`,
                                            severity: 'warning'
                                          });
                                          return;
                                        }
                                        
                                        const newBoxQty = currentBoxQty + 1;
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                boxQuantity: newBoxQty,
                                                quantity: newBoxQty + (i.singleQuantity || 0),
                                                fulfilledQuantity: !isEditMode ? newBoxQty + (i.singleQuantity || 0) : i.fulfilledQuantity,
                                                fulfilledBoxQuantity: !isEditMode ? newBoxQty : (i.fulfilledBoxQuantity || 0),
                                                fulfilledSingleQuantity: !isEditMode ? (i.singleQuantity || 0) : (i.fulfilledSingleQuantity || 0)
                                              }
                                            : i
                                        );
                                        
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                    >
                                      <AddCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ minWidth: '40px' }}>Single:</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton 
                                      color="error" 
                                      size="small"
                                      disabled={isProductDeleted || (item.singleQuantity || 0) === 0}
                                      onClick={() => {
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                singleQuantity: Math.max(0, (i.singleQuantity || 0) - 1),
                                                quantity: (i.boxQuantity || 0) + Math.max(0, (i.singleQuantity || 0) - 1),
                                                fulfilledQuantity: !isEditMode ? (i.boxQuantity || 0) + Math.max(0, (i.singleQuantity || 0) - 1) : i.fulfilledQuantity,
                                                fulfilledBoxQuantity: !isEditMode ? (i.boxQuantity || 0) : (i.fulfilledBoxQuantity || 0),
                                                fulfilledSingleQuantity: !isEditMode ? Math.max(0, (i.singleQuantity || 0) - 1) : (i.fulfilledSingleQuantity || 0)
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                    >
                                      <RemoveCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={item.singleQuantity || 0}
                                      onChange={(e) => {
                                        const newValue = parseInt(e.target.value, 10);
                                        if (isNaN(newValue) || newValue < 0) return;
                                        
                                        const maxSingleStock = getMaxAllowedQuantity(item.productId, 'single');
                                        const validSingleQty = Math.min(newValue, maxSingleStock);
                                        
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                singleQuantity: validSingleQty,
                                                quantity: (i.boxQuantity || 0) + validSingleQty,
                                                fulfilledQuantity: !isEditMode ? (i.boxQuantity || 0) + validSingleQty : i.fulfilledQuantity,
                                                fulfilledBoxQuantity: !isEditMode ? (i.boxQuantity || 0) : (i.fulfilledBoxQuantity || 0),
                                                fulfilledSingleQuantity: !isEditMode ? validSingleQty : (i.fulfilledSingleQuantity || 0)
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                      inputProps={{
                                        min: 0,
                                        max: getMaxAllowedQuantity(item.productId, 'single'),
                                        style: { textAlign: 'center' }
                                      }}
                                      sx={{ width: '60px', mx: 1 }}
                                      disabled={isProductDeleted}
                                      onFocus={(e) => e.target.select()}
                                    />
                                    <IconButton 
                                      color="primary" 
                                      size="small"
                                      disabled={isProductDeleted || (() => {
                                        const maxAllowed = getMaxAllowedQuantity(item.productId, 'single');
                                        return (item.singleQuantity || 0) >= maxAllowed;
                                      })()}
                                      onClick={() => {
                                        const maxSingleStock = getMaxAllowedQuantity(item.productId, 'single');
                                        const newSingleQty = Math.min((item.singleQuantity || 0) + 1, maxSingleStock);
                                        
                                        // Safety check
                                        if (newSingleQty > maxSingleStock) return;
                                        
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                singleQuantity: newSingleQty,
                                                quantity: (i.boxQuantity || 0) + newSingleQty,
                                                fulfilledQuantity: !isEditMode ? (i.boxQuantity || 0) + newSingleQty : i.fulfilledQuantity,
                                                fulfilledBoxQuantity: !isEditMode ? (i.boxQuantity || 0) : (i.fulfilledBoxQuantity || 0),
                                                fulfilledSingleQuantity: !isEditMode ? newSingleQty : (i.fulfilledSingleQuantity || 0)
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                    >
                                      <AddCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <IconButton
                                  color="error"
                                  size="small"
                                  disabled={isProductDeleted || item.quantity <= 0}
                                  onClick={() => {
                                    if (item.quantity === 1) {
                                      handleRemoveItem(index);
                                    } else {
                                      const updatedItems = formData.items.map((i, idx) => 
                                        idx === index 
                                          ? {
                                              ...i, 
                                              quantity: i.quantity - 1, 
                                              fulfilledQuantity: !isEditMode ? i.quantity - 1 : i.fulfilledQuantity
                                            }
                                          : i
                                      );
                                      const newFormData = { ...formData, items: updatedItems };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                    }
                                  }}
                                >
                                  <RemoveCircleOutlineIcon fontSize="small" />
                                </IconButton>
                                <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
                                <IconButton 
                                  color="primary" 
                                  size="small"
                                  disabled={isProductDeleted || (() => {
                                    const orderType = item.orderType as 'box' | 'single' | 'both';
                                    const stockType = orderType === 'both' ? 'box' : (orderType || 'single');
                                    const maxAllowed = getMaxAllowedQuantity(item.productId, stockType);
                                    return item.quantity >= maxAllowed;
                                  })()}
                                  onClick={() => {
                                    const orderType = item.orderType as 'box' | 'single' | 'both';
                                    const stockType = orderType === 'both' ? 'box' : (orderType || 'single');
                                    const maxAllowed = getMaxAllowedQuantity(item.productId, stockType);
                                    if (item.quantity < maxAllowed) {
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                quantity: i.quantity + 1,
                                                fulfilledQuantity: !isEditMode ? i.quantity + 1 : i.fulfilledQuantity
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }
                                  }}
                                >
                                  <AddCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </TableCell>
                        )}
                        {isEditMode && !isMobile && (
                          <TableCell align="right">
                            {item.orderType === 'both' ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ minWidth: '40px' }}>Box:</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton 
                                      color="error" 
                                      size="small"
                                      disabled={isProductDeleted || (item.fulfilledBoxQuantity || 0) <= 0}
                                      onClick={() => {
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                fulfilledBoxQuantity: Math.max(0, (i.fulfilledBoxQuantity || 0) - 1),
                                                fulfilledQuantity: Math.max(0, (i.fulfilledBoxQuantity || 0) - 1) + (i.fulfilledSingleQuantity || 0)
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                    >
                                      <RemoveCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={item.fulfilledBoxQuantity || 0}
                                      onChange={(e) => {
                                        const newValue = parseInt(e.target.value, 10);
                                        if (isNaN(newValue) || newValue < 0) return;
                                        
                                        const maxFulfilledBox = item.boxQuantity || 0;
                                        const validFulfilledBoxQty = Math.min(newValue, maxFulfilledBox);
                                        
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                fulfilledBoxQuantity: validFulfilledBoxQty,
                                                fulfilledQuantity: validFulfilledBoxQty + (i.fulfilledSingleQuantity || 0)
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                      inputProps={{
                                        min: 0,
                                        max: item.boxQuantity || 0,
                                        style: { textAlign: 'center' }
                                      }}
                                      sx={{ width: '60px', mx: 1 }}
                                      onFocus={(e) => e.target.select()}
                                    />
                                    <IconButton 
                                      color="primary" 
                                      size="small"
                                      disabled={isProductDeleted || (item.fulfilledBoxQuantity || 0) >= (item.boxQuantity || 0)}
                                      onClick={() => {
                                        const newFulfilledBoxQty = Math.min((item.fulfilledBoxQuantity || 0) + 1, item.boxQuantity || 0);
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                fulfilledBoxQuantity: newFulfilledBoxQty,
                                                fulfilledQuantity: newFulfilledBoxQty + (i.fulfilledSingleQuantity || 0)
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                    >
                                      <AddCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ minWidth: '40px' }}>Single:</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton 
                                      color="error" 
                                      size="small"
                                      disabled={isProductDeleted || (item.fulfilledSingleQuantity || 0) <= 0}
                                      onClick={() => {
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                fulfilledSingleQuantity: Math.max(0, (i.fulfilledSingleQuantity || 0) - 1),
                                                fulfilledQuantity: (i.fulfilledBoxQuantity || 0) + Math.max(0, (i.fulfilledSingleQuantity || 0) - 1)
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                    >
                                      <RemoveCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={item.fulfilledSingleQuantity || 0}
                                      onChange={(e) => {
                                        const newValue = parseInt(e.target.value, 10);
                                        if (isNaN(newValue) || newValue < 0) return;
                                        
                                        const maxFulfilledSingle = item.singleQuantity || 0;
                                        const validFulfilledSingleQty = Math.min(newValue, maxFulfilledSingle);
                                        
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                fulfilledSingleQuantity: validFulfilledSingleQty,
                                                fulfilledQuantity: (i.fulfilledBoxQuantity || 0) + validFulfilledSingleQty
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                      inputProps={{
                                        min: 0,
                                        max: item.singleQuantity || 0,
                                        style: { textAlign: 'center' }
                                      }}
                                      sx={{ width: '60px', mx: 1 }}
                                      onFocus={(e) => e.target.select()}
                                    />
                                    <IconButton 
                                      color="primary" 
                                      size="small"
                                      disabled={isProductDeleted || (item.fulfilledSingleQuantity || 0) >= (item.singleQuantity || 0)}
                                      onClick={() => {
                                        const newFulfilledSingleQty = Math.min((item.fulfilledSingleQuantity || 0) + 1, item.singleQuantity || 0);
                                        const updatedItems = formData.items.map((i, idx) => 
                                          idx === index 
                                            ? {
                                                ...i, 
                                                fulfilledSingleQuantity: newFulfilledSingleQty,
                                                fulfilledQuantity: (i.fulfilledBoxQuantity || 0) + newFulfilledSingleQty
                                              }
                                            : i
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }}
                                    >
                                      <AddCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <IconButton
                                  color="error"
                                  size="small"
                                  disabled={isProductDeleted || item.fulfilledQuantity <= 0}
                                  onClick={() => {
                                    if (item.fulfilledQuantity > 0) {
                                      const updatedItems = formData.items.map((i, idx) => 
                                        idx === index 
                                          ? {...i, fulfilledQuantity: i.fulfilledQuantity - 1}
                                          : i
                                      );
                                      const newFormData = { ...formData, items: updatedItems };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                    }
                                  }}
                                >
                                  <RemoveCircleOutlineIcon fontSize="small" />
                                </IconButton>
                                <Typography sx={{ mx: 1 }}>{item.fulfilledQuantity}</Typography>
                                <IconButton 
                                  color="primary" 
                                  size="small"
                                  disabled={isProductDeleted || (() => {
                                    const product = products.find(p => p.id === item.productId);
                                    const currentStock = product ? product.stockCount : 0;
                                    const originalItem = orderData?.items?.find((i: any) => i.id === item.id);
                                    const originalFulfilledQty = originalItem ?
                                      (originalItem.fulfilled_quantity !== undefined ? Number(originalItem.fulfilled_quantity) : 0) : 0;
                                    const maxAllowed = originalFulfilledQty + currentStock;
                                    return item.fulfilledQuantity >= maxAllowed;
                                  })()}
                                  onClick={() => {
                                    const product = products.find(p => p.id === item.productId);
                                    const currentStock = product ? product.stockCount : 0;
                                    const originalItem = orderData?.items?.find((i: any) => i.id === item.id);
                                    const originalFulfilledQty = originalItem ? 
                                      (originalItem.fulfilled_quantity !== undefined ? Number(originalItem.fulfilled_quantity) : 0) : 0;
                                    const maxAllowed = originalFulfilledQty + currentStock;
                                    
                                    if (item.fulfilledQuantity < maxAllowed) {
                                      const updatedItems = formData.items.map((i, idx) => 
                                        idx === index 
                                          ? {...i, fulfilledQuantity: i.fulfilledQuantity + 1}
                                          : i
                                      );
                                      const newFormData = { ...formData, items: updatedItems };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                    }
                                  }}
                                >
                                  <AddCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={item.unitPrice}
                              disabled={isProductDeleted}
                              onChange={(e) => {
                                const updatedItems = formData.items.map((i, idx) =>
                                  idx === index
                                    ? {...i, unitPrice: Number(e.target.value)}
                                    : i
                                );
                                const newFormData = { ...formData, items: updatedItems };
                                setFormData(newFormData);
                                setHasUnsavedChanges(true);
                                debouncedAutosave(newFormData);
                              }}
                              inputProps={{
                                min: 0.01,
                                step: 0.01,
                                style: { textAlign: 'right' }
                              }}
                              sx={{ width: isTablet ? '80px' : '100px' }}
                              onFocus={(e) => e.target.select()}
                            />
                          </TableCell>
                        )}
                        {!isTablet && (
                          <TableCell align="right">
                            {(() => {
                              // Use stored product data if available (for existing order items), otherwise use current product data
                              let qtyInBox = 1;
                              if (item.storedProductData && item.storedProductData.qty_in_box) {
                                qtyInBox = item.storedProductData.qty_in_box;
                              } else {
                                const product = products.find(p => p.id === item.productId);
                                qtyInBox = product ? product.qtyInBox : 1;
                              }
                              
                              switch (item.orderType) {
                                case 'single':
                                  return item.unitPrice.toFixed(2);
                                case 'box':
                                  return (item.unitPrice * qtyInBox).toFixed(2);
                                case 'both':
                                  return (item.unitPrice * qtyInBox).toFixed(2); // Using box pricing for bulk orders
                                default:
                                  return (item.unitPrice * qtyInBox).toFixed(2);
                              }
                            })()}
                          </TableCell>
                        )}
                        {!isTablet && <TableCell align="right">{item.discount}%</TableCell>}
                        {!isTablet && <TableCell align="right">{item.tax}%</TableCell>}
                        <TableCell align="right">
                          {(() => {
                            const quantity = Number(item.fulfilledQuantity) || 0;
                            const unitPrice = Number(item.unitPrice) || 0;
                            const discount = Number(item.discount) || 0;
                            const tax = Number(item.tax) || 0;
                            
                            let basePrice: number;
                            // Use stored product data if available (for existing order items), otherwise use current product data
                            let qtyInBox = 1;
                            if (item.storedProductData && item.storedProductData.qty_in_box) {
                              qtyInBox = item.storedProductData.qty_in_box;
                            } else {
                              const product = products.find(p => p.id === item.productId);
                              qtyInBox = product ? product.qtyInBox : 1;
                            }
                            
                            switch (item.orderType) {
                              case 'single':
                                basePrice = unitPrice;
                                return formatCurrency(quantity * basePrice * (1 - discount / 100) * (1 + tax / 100));
                              case 'box':
                                basePrice = unitPrice * qtyInBox;
                                return formatCurrency(quantity * basePrice * (1 - discount / 100) * (1 + tax / 100));
                              case 'both':
                                // Calculate separately for boxes and singles
                                const boxQty = item.fulfilledBoxQuantity || 0;
                                const singleQty = item.fulfilledSingleQuantity || 0;
                                const boxPrice = unitPrice * qtyInBox;
                                const singlePrice = unitPrice;
                                const boxSubtotal = boxQty * boxPrice * (1 - discount / 100);
                                const singleSubtotal = singleQty * singlePrice * (1 - discount / 100);
                                const total = (boxSubtotal + singleSubtotal) * (1 + tax / 100);
                                return formatCurrency(total);
                              default:
                                // Default to single pricing if order type is not properly set
                                basePrice = unitPrice;
                                return formatCurrency(quantity * basePrice * (1 - discount / 100) * (1 + tax / 100));
                            }
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip
                            title={formData.items.length === 1 ? "Add another item before deleting this product or else delete the whole order from order page" : ""}
                            arrow
                          >
                            <span>
                              <IconButton
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                                size="small"
                                disabled={formData.items.length === 1}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                    })}
                    {formData.items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isMobile ? 3 : (isTablet ? 6 : 9)} align="center">
                          No items added to this order
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Refunds section */}
              {refunds.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'error.main' }}>
                    Refunds
                  </Typography>
                  {refunds.map((refund: any) => (
                    <Paper key={refund.id} sx={{ p: 2, mb: 1, backgroundColor: '#fff5f5' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                            Refund
                          </Typography>
                          {refund.reason && (
                            <Typography variant="body2" color="text.secondary">
                              {refund.reason}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                            -{formatCurrency(parseFloat(refund.refund_amount))}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditRefund(refund)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteRefund(refund.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>

          {/* Order Summary */}
          <Box sx={{ flex: '1 1 30%', minWidth: '300px' }}>
            <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              
              {selectedCustomer && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedCustomer.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCustomer.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCustomer.phone}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={formData.status.charAt(0).toUpperCase() + formData.status.slice(1)}
                    color={
                      formData.status === 'completed' ? 'success' :
                      formData.status === 'shipped' ? 'primary' :
                      formData.status === 'processing' ? 'info' :
                      formData.status === 'pending' ? 'warning' :
                      'error'
                    }
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={formData.paymentStatus.charAt(0).toUpperCase() + formData.paymentStatus.slice(1)}
                    color={
                      formData.paymentStatus === 'paid' ? 'success' :
                      formData.paymentStatus === 'pending' ? 'warning' :
                      formData.paymentStatus === 'overdue' ? 'error' :
                      formData.paymentStatus === 'cancelled' ? 'error' :
                      'default'
                    }
                    size="small"
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">Subtotal:</Typography>
                  <Typography variant="body1">{formatCurrency(calculateSubtotal())}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">Tax:</Typography>
                  <Typography variant="body1">{formatCurrency(calculateTax())}</Typography>
                </Box>
                {calculateRefundTotal() > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" color="error">Refunds:</Typography>
                    <Typography variant="body1" color="error">-{formatCurrency(calculateRefundTotal())}</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6">{formatCurrency(calculateTotal())}</Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (isEditMode ? 'Update Order' : 'Create Order')}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Add Product Dialog */}
      <Dialog 
        open={productDialogOpen} 
        onClose={() => setProductDialogOpen(false)} 
        maxWidth={isMobile ? false : "lg"} 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            height: isMobile ? '100%' : '90vh',
            maxHeight: isMobile ? 'none' : '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6">Add Products to Order</Typography>
          {isMobile && (
            <IconButton onClick={() => setProductDialogOpen(false)}>
              <ClearIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <TextField
            label="Search Products"
            variant="outlined"
            fullWidth
            value={searchTerm}
            sx={{ mb: 2, flexShrink: 0 }}
            onChange={(e) => {
              const newSearchTerm = e.target.value;
              setSearchTerm(newSearchTerm);
              
              // Real-time filtering based on search term and selected category
              let filtered = products;
              
              if (selectedCategory !== 'all') {
                filtered = filtered.filter(product => product.category.id === selectedCategory);
              }
              
              if (newSearchTerm.trim() !== '') {
                filtered = filtered.filter(product => 
                  product.name.toLowerCase().includes(newSearchTerm.toLowerCase()) ||
                  product.sku.toLowerCase().includes(newSearchTerm.toLowerCase())
                );
              }
              
              setFilteredProducts(filtered);
            }}
            InputProps={{
              endAdornment: searchTerm ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchTerm('');
                    
                    // Reset search filtering but keep category filtering
                    let filtered = products;
                    if (selectedCategory !== 'all') {
                      filtered = filtered.filter(product => product.category.id === selectedCategory);
                    }
                    setFilteredProducts(filtered);
                  }}
                >
                  <ClearIcon />
                </IconButton>
              ) : null
            }}
          />
          
          {/* Category Tabs */}
          <Paper sx={{ mb: 2, flexShrink: 0 }}>
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {categories.map((category) => (
                <Tab key={category.id} label={category.name} value={category.id} />
              ))}
            </Tabs>
          </Paper>
          
          <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
            {filteredProducts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No products found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search or filter criteria
                </Typography>
              </Box>
            ) : (
              <>
                {/* Desktop/Laptop Table View */}
                {!isTablet && (
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Image</TableCell>
                        <TableCell>Product Information</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Box Price</TableCell>
                        <TableCell align="right">Stock</TableCell>
                        <TableCell align="center">Type</TableCell>
                        <TableCell align="center">Quantity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredProducts.map((product) => {
                        // Check if product is already in order
                        const existingItem = formData.items.find(item => item.productId === product.id);
                        const currentQty = existingItem ? existingItem.quantity : 0;
                        
                        return (
                          <TableRow key={product.id}>
                            <TableCell>
                              <Box
                                component="img"
                                src={getImageUrl(product.image)}
                                alt={product.name}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = getImageUrl(null);
                                }}
                                onClick={() => handleImagePreview(getImageUrl(product.image), product.name)}
                                sx={{
                                  width: 50,
                                  height: 50,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{product.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{product.sku}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {existingItem?.storedProductData?.unit_size || product.unit_size} x {existingItem?.storedProductData?.qty_in_box || product.qtyInBox} ({existingItem?.storedProductData?.unit_type || product.unitType})
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={product.category?.name || 'Uncategorized'} size="small" />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={existingItem?.unitPrice || product.unitPrice}
                                onChange={(e) => {
                                  if (existingItem) {
                                    const updatedItems = formData.items.map(item => 
                                      item.productId === product.id 
                                        ? {...item, unitPrice: Number(e.target.value)}
                                        : item
                                    );
                                    const newFormData = {
                                      ...formData,
                                      items: updatedItems
                                    };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                  }
                                }}
                                inputProps={{ 
                                  min: 0.01, 
                                  step: 0.01,
                                  style: { textAlign: 'right' }
                                }}
                                sx={{ width: '100px' }}
                                disabled={!existingItem}
                                onFocus={(e) => e.target.select()}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency((existingItem?.unitPrice || product.unitPrice) * (existingItem?.storedProductData?.qty_in_box || product.qtyInBox))}
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2">{product.stockCount} (B)</Typography>
                                <Typography variant="body2" color="text.secondary">{product.stockSingle} (S)</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Select
                                size="small"
                                value={existingItem?.orderType || productOrderTypes[product.id] || 'box'}
                                onChange={async (e) => {
                                  const newOrderType = e.target.value as 'box' | 'single' | 'both';
                                  if (existingItem) {
                                    const oldOrderType = existingItem.orderType || 'box';

                                    // DISABLED: Stock restoration is now handled automatically by the backend
                                    // when it detects order type changes during autosave/update
                                    //
                                    // if (oldOrderType !== newOrderType && (existingItem.quantity > 0 || (existingItem.boxQuantity || 0) > 0 || (existingItem.singleQuantity || 0) > 0)) {
                                    //   await restoreStockForOrderTypeChange(
                                    //     existingItem.productId,
                                    //     oldOrderType,
                                    //     existingItem.quantity,
                                    //     existingItem.boxQuantity,
                                    //     existingItem.singleQuantity
                                    //   );
                                    // }

                                    const updatedItems = formData.items.map(item => {
                                      if (item.productId === product.id) {
                                        // According to requirements: When switching order types,
                                        // stock is restored and quantities are reset to zero
                                        let newQuantity = 0;
                                        let newFulfilledQuantity = 0;
                                        let newBoxQuantity: number | undefined;
                                        let newSingleQuantity: number | undefined;
                                        let newFulfilledBoxQuantity: number | undefined;
                                        let newFulfilledSingleQuantity: number | undefined;
                                        
                                        if (newOrderType === 'both') {
                                          // Switching TO 'both' type - set both quantities to 0
                                          newBoxQuantity = 0;
                                          newSingleQuantity = 0;
                                          newFulfilledBoxQuantity = 0;
                                          newFulfilledSingleQuantity = 0;
                                          newQuantity = 0; // Total quantity is sum of box + single
                                          newFulfilledQuantity = 0;
                                        } else {
                                          // Switching TO 'box' or 'single' type - set main quantity to 0
                                          newQuantity = 0;
                                          newFulfilledQuantity = 0;
                                          // Clear box/single quantities for non-'both' types
                                          newBoxQuantity = undefined;
                                          newSingleQuantity = undefined;
                                          newFulfilledBoxQuantity = undefined;
                                          newFulfilledSingleQuantity = undefined;
                                        }
                                        
                                        return {
                                          ...item, 
                                          orderType: newOrderType,
                                          boxQuantity: newBoxQuantity,
                                          singleQuantity: newSingleQuantity,
                                          fulfilledBoxQuantity: newFulfilledBoxQuantity,
                                          fulfilledSingleQuantity: newFulfilledSingleQuantity,
                                          quantity: newQuantity,
                                          fulfilledQuantity: newFulfilledQuantity
                                        };
                                      }
                                      return item;
                                    });
                                    const newFormData = {
                                      ...formData,
                                      items: updatedItems
                                    };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                  } else {
                                    // For new items (not existing), reset product-level state
                                    setProductOrderTypes({
                                      ...productOrderTypes,
                                      [product.id]: newOrderType
                                    });
                                    // Reset quantities when switching types for new products
                                    setProductBoxQuantities({
                                      ...productBoxQuantities,
                                      [product.id]: 0
                                    });
                                    setProductSingleQuantities({
                                      ...productSingleQuantities,
                                      [product.id]: 0
                                    });
                                  }
                                }}
                                sx={{ minWidth: 80 }}
                              >
                                <MenuItem value="box">Box</MenuItem>
                                <MenuItem value="single">Single</MenuItem>
                                <MenuItem value="both">Both</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell align="center">
                              {(existingItem?.orderType || productOrderTypes[product.id]) === 'both' ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ minWidth: '30px' }}>Box:</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <IconButton 
                                        color="error" 
                                        size="small"
                                        disabled={(existingItem?.boxQuantity || productBoxQuantities[product.id] || 0) === 0}
                                        onClick={() => {
                                          if (existingItem) {
                                            const newBoxQty = Math.max(0, (existingItem.boxQuantity || 0) - 1);
                                            const currentSingleQty = existingItem.singleQuantity || 0;
                                            
                                            // If both quantities become 0, remove the item
                                            if (newBoxQty === 0 && currentSingleQty === 0) {
                                              const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            } else {
                                              const updatedItems = formData.items.map(item => 
                                                item.productId === product.id 
                                                  ? {
                                                      ...item, 
                                                      boxQuantity: newBoxQty,
                                                      quantity: newBoxQty + currentSingleQty,
                                                      fulfilledQuantity: newBoxQty + currentSingleQty,
                                                      fulfilledBoxQuantity: newBoxQty
                                                    }
                                                  : item
                                              );
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            }
                                          } else {
                                            setProductBoxQuantities({
                                              ...productBoxQuantities,
                                              [product.id]: Math.max(0, (productBoxQuantities[product.id] || 0) - 1)
                                            });
                                          }
                                        }}
                                      >
                                        <RemoveCircleOutlineIcon fontSize="small" />
                                      </IconButton>
                                      <TextField
                                        type="number"
                                        size="small"
                                        value={existingItem?.boxQuantity || productBoxQuantities[product.id] || 0}
                                        onChange={(e) => {
                                          const newValue = parseInt(e.target.value, 10);
                                          if (isNaN(newValue) || newValue < 0) return;
                                          
                                          const maxBoxStock = getMaxAllowedQuantity(product.id, 'box');
                                          const validBoxQty = Math.min(newValue, maxBoxStock);
                                          
                                          if (existingItem) {
                                            const currentSingleQty = existingItem.singleQuantity || 0;
                                            
                                            // If both quantities become 0, remove the item
                                            if (validBoxQty === 0 && currentSingleQty === 0) {
                                              const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            } else {
                                              const updatedItems = formData.items.map(item => 
                                                item.productId === product.id 
                                                  ? {
                                                      ...item, 
                                                      boxQuantity: validBoxQty,
                                                      quantity: validBoxQty + currentSingleQty,
                                                      fulfilledQuantity: validBoxQty + currentSingleQty,
                                                      fulfilledBoxQuantity: validBoxQty
                                                    }
                                                  : item
                                              );
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            }
                                          } else {
                                            setProductBoxQuantities({
                                              ...productBoxQuantities,
                                              [product.id]: validBoxQty
                                            });

                                            const currentSingleQty = productSingleQuantities[product.id] || 0;
                                            const existingItemIndex = formData.items.findIndex(item => item.productId === product.id);

                                            // Create new item if no existing item and quantity > 0
                                            if (existingItemIndex < 0 && (validBoxQty > 0 || currentSingleQty > 0)) {
                                              const orderType = productOrderTypes[product.id] || 'both';
                                              const newItem: OrderItem = {
                                                productId: product.id,
                                                productName: product.name,
                                                sku: product.sku,
                                                quantity: validBoxQty + currentSingleQty,
                                                fulfilledQuantity: validBoxQty + currentSingleQty,
                                                unitPrice: Number(product.unitPrice) || 0,
                                                discount: 0,
                                                tax: Number(product.tax) || 0,
                                                total: Number(product.unitPrice) * (1 + Number(product.tax) / 100),
                                                orderType: orderType,
                                                boxQuantity: validBoxQty,
                                                singleQuantity: currentSingleQty,
                                                fulfilledBoxQuantity: validBoxQty,
                                                fulfilledSingleQuantity: currentSingleQty,
                                              };
                                              const newFormData = { ...formData, items: [...formData.items, newItem] };
                                              setFormData(newFormData);
                                              setHasUnsavedChanges(true);
                                              debouncedAutosave(newFormData);
                                            } else if (existingItemIndex >= 0) {
                                              // Update existing item
                                              const updatedItems = formData.items.map((item, idx) =>
                                                idx === existingItemIndex
                                                  ? {
                                                      ...item,
                                                      boxQuantity: validBoxQty,
                                                      quantity: validBoxQty + (item.singleQuantity || 0),
                                                      fulfilledQuantity: validBoxQty + (item.singleQuantity || 0),
                                                      fulfilledBoxQuantity: validBoxQty,
                                                    }
                                                  : item
                                              );
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            }
                                          }
                                        }}
                                        inputProps={{
                                          min: 0,
                                          max: product.stockCount || 0,
                                          style: { textAlign: 'center' }
                                        }}
                                        sx={{ width: '60px', mx: 1 }}
                                        onFocus={(e) => e.target.select()}
                                      />
                                      <IconButton 
                                        color="primary" 
                                        size="small"
                                        disabled={(() => {
                                          const currentBoxQty = existingItem?.boxQuantity || productBoxQuantities[product.id] || 0;
                                          const maxAllowed = getMaxAllowedQuantity(product.id, 'box');
                                          return currentBoxQty >= maxAllowed;
                                        })()}
                                        onClick={() => {
                                          if (existingItem) {
                                            const maxBoxStock = getMaxAllowedQuantity(product.id, 'box');
                                            const newBoxQty = Math.min((existingItem.boxQuantity || 0) + 1, maxBoxStock);
                                            
                                            // Safety check
                                            if (newBoxQty > maxBoxStock) return;
                                            
                                            const currentSingleQty = existingItem.singleQuantity || 0;
                                            const updatedItems = formData.items.map(item => 
                                              item.productId === product.id 
                                                ? {
                                                    ...item, 
                                                    boxQuantity: newBoxQty,
                                                    quantity: newBoxQty + currentSingleQty,
                                                    fulfilledQuantity: newBoxQty + currentSingleQty,
                                                    fulfilledBoxQuantity: newBoxQty
                                                  }
                                                : item
                                            );
                                            const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                          } else {
                                            const maxBoxStock = getMaxAllowedQuantity(product.id, 'box');
                                            const currentBoxQty = productBoxQuantities[product.id] || 0;
                                            const newBoxQty = Math.min(currentBoxQty + 1, maxBoxStock);
                                            
                                            // Safety check
                                            if (newBoxQty > maxBoxStock) return;
                                            
                                            const currentSingleQty = productSingleQuantities[product.id] || 0;
                                            setProductBoxQuantities({
                                              ...productBoxQuantities,
                                              [product.id]: newBoxQty
                                            });

                                            const existingItemIndex = formData.items.findIndex(item => item.productId === product.id);

                                            // Create new item if no existing item and quantity > 0
                                            if (existingItemIndex < 0 && (newBoxQty > 0 || currentSingleQty > 0)) {
                                              const orderType = productOrderTypes[product.id] || 'both';
                                              const newItem: OrderItem = {
                                                productId: product.id,
                                                productName: product.name,
                                                sku: product.sku,
                                                quantity: newBoxQty + currentSingleQty,
                                                fulfilledQuantity: newBoxQty + currentSingleQty,
                                                unitPrice: Number(product.unitPrice) || 0,
                                                discount: 0,
                                                tax: Number(product.tax) || 0,
                                                total: Number(product.unitPrice) * (1 + Number(product.tax) / 100),
                                                orderType: orderType,
                                                boxQuantity: newBoxQty,
                                                singleQuantity: currentSingleQty,
                                                fulfilledBoxQuantity: newBoxQty,
                                                fulfilledSingleQuantity: currentSingleQty,
                                              };
                                              const newFormData = { ...formData, items: [...formData.items, newItem] };
                                              setFormData(newFormData);
                                              setHasUnsavedChanges(true);
                                              debouncedAutosave(newFormData);
                                            } else if (existingItemIndex >= 0) {
                                              // Update existing item
                                              const updatedItems = formData.items.map((item, idx) =>
                                                idx === existingItemIndex
                                                  ? {
                                                      ...item,
                                                      boxQuantity: newBoxQty,
                                                      quantity: newBoxQty + (item.singleQuantity || 0),
                                                      fulfilledQuantity: newBoxQty + (item.singleQuantity || 0),
                                                      fulfilledBoxQuantity: newBoxQty,
                                                    }
                                                  : item
                                              );
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            }
                                          }
                                        }}
                                      >
                                        <AddCircleOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ minWidth: '30px' }}>Single:</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <IconButton 
                                        color="error" 
                                        size="small"
                                        disabled={(existingItem?.singleQuantity || productSingleQuantities[product.id] || 0) === 0}
                                        onClick={() => {
                                          if (existingItem) {
                                            const newSingleQty = Math.max(0, (existingItem.singleQuantity || 0) - 1);
                                            const currentBoxQty = existingItem.boxQuantity || 0;
                                            
                                            // If both quantities become 0, remove the item
                                            if (currentBoxQty === 0 && newSingleQty === 0) {
                                              const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            } else {
                                              const updatedItems = formData.items.map(item => 
                                                item.productId === product.id 
                                                  ? {
                                                      ...item, 
                                                      singleQuantity: newSingleQty,
                                                      quantity: currentBoxQty + newSingleQty,
                                                      fulfilledQuantity: currentBoxQty + newSingleQty,
                                                      fulfilledSingleQuantity: newSingleQty
                                                    }
                                                  : item
                                              );
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            }
                                          } else {
                                            setProductSingleQuantities({
                                              ...productSingleQuantities,
                                              [product.id]: Math.max(0, (productSingleQuantities[product.id] || 0) - 1)
                                            });
                                          }
                                        }}
                                      >
                                        <RemoveCircleOutlineIcon fontSize="small" />
                                      </IconButton>
                                      <TextField
                                        type="number"
                                        size="small"
                                        value={existingItem?.singleQuantity || productSingleQuantities[product.id] || 0}
                                        onChange={(e) => {
                                          const newValue = parseInt(e.target.value, 10);
                                          if (isNaN(newValue) || newValue < 0) return;
                                          
                                          const maxSingleStock = getMaxAllowedQuantity(product.id, 'single');
                                          const validSingleQty = Math.min(newValue, maxSingleStock);
                                          
                                          if (existingItem) {
                                            const currentBoxQty = existingItem.boxQuantity || 0;
                                            
                                            // If both quantities become 0, remove the item
                                            if (currentBoxQty === 0 && validSingleQty === 0) {
                                              const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            } else {
                                              const updatedItems = formData.items.map(item => 
                                                item.productId === product.id 
                                                  ? {
                                                      ...item, 
                                                      singleQuantity: validSingleQty,
                                                      quantity: currentBoxQty + validSingleQty,
                                                      fulfilledQuantity: currentBoxQty + validSingleQty,
                                                      fulfilledSingleQuantity: validSingleQty
                                                    }
                                                  : item
                                              );
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            }
                                          } else {
                                            setProductSingleQuantities({
                                              ...productSingleQuantities,
                                              [product.id]: validSingleQty
                                            });

                                            const currentBoxQty = productBoxQuantities[product.id] || 0;
                                            const existingItemIndex = formData.items.findIndex(item => item.productId === product.id);

                                            // Create new item if no existing item and quantity > 0
                                            if (existingItemIndex < 0 && (currentBoxQty > 0 || validSingleQty > 0)) {
                                              const orderType = productOrderTypes[product.id] || 'both';
                                              const newItem: OrderItem = {
                                                productId: product.id,
                                                productName: product.name,
                                                sku: product.sku,
                                                quantity: currentBoxQty + validSingleQty,
                                                fulfilledQuantity: currentBoxQty + validSingleQty,
                                                unitPrice: Number(product.unitPrice) || 0,
                                                discount: 0,
                                                tax: Number(product.tax) || 0,
                                                total: Number(product.unitPrice) * (1 + Number(product.tax) / 100),
                                                orderType: orderType,
                                                boxQuantity: currentBoxQty,
                                                singleQuantity: validSingleQty,
                                                fulfilledBoxQuantity: currentBoxQty,
                                                fulfilledSingleQuantity: validSingleQty,
                                              };
                                              const newFormData = { ...formData, items: [...formData.items, newItem] };
                                              setFormData(newFormData);
                                              setHasUnsavedChanges(true);
                                              debouncedAutosave(newFormData);
                                            } else if (existingItemIndex >= 0) {
                                              // Update existing item
                                              const updatedItems = formData.items.map((item, idx) =>
                                                idx === existingItemIndex
                                                  ? {
                                                      ...item,
                                                      singleQuantity: validSingleQty,
                                                      quantity: (item.boxQuantity || 0) + validSingleQty,
                                                      fulfilledQuantity: (item.boxQuantity || 0) + validSingleQty,
                                                      fulfilledSingleQuantity: validSingleQty,
                                                    }
                                                  : item
                                              );
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            }
                                          }
                                        }}
                                        inputProps={{
                                          min: 0,
                                          max: product.stockSingle || 0,
                                          style: { textAlign: 'center' }
                                        }}
                                        sx={{ width: '60px', mx: 1 }}
                                        onFocus={(e) => e.target.select()}
                                      />
                                      <IconButton 
                                        color="primary" 
                                        size="small"
                                        disabled={(() => {
                                          const currentSingleQty = existingItem?.singleQuantity || productSingleQuantities[product.id] || 0;
                                          const maxAllowed = getMaxAllowedQuantity(product.id, 'single');
                                          return currentSingleQty >= maxAllowed;
                                        })()}
                                        onClick={() => {
                                          if (existingItem) {
                                            const maxSingleStock = getMaxAllowedQuantity(product.id, 'single');
                                            const newSingleQty = Math.min((existingItem.singleQuantity || 0) + 1, maxSingleStock);
                                            
                                            // Safety check
                                            if (newSingleQty > maxSingleStock) return;
                                            
                                            const currentBoxQty = existingItem.boxQuantity || 0;
                                            const updatedItems = formData.items.map(item => 
                                              item.productId === product.id 
                                                ? {
                                                    ...item, 
                                                    singleQuantity: newSingleQty,
                                                    quantity: currentBoxQty + newSingleQty,
                                                    fulfilledQuantity: currentBoxQty + newSingleQty,
                                                    fulfilledSingleQuantity: newSingleQty
                                                  }
                                                : item
                                            );
                                            const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                          } else {
                                            const maxSingleStock = getMaxAllowedQuantity(product.id, 'single');
                                            const currentSingleQty = productSingleQuantities[product.id] || 0;
                                            const newSingleQty = Math.min(currentSingleQty + 1, maxSingleStock);
                                            
                                            // Safety check
                                            if (newSingleQty > maxSingleStock) return;
                                            
                                            const currentBoxQty = productBoxQuantities[product.id] || 0;
                                            setProductSingleQuantities({
                                              ...productSingleQuantities,
                                              [product.id]: newSingleQty
                                            });

                                            const existingItemIndex = formData.items.findIndex(item => item.productId === product.id);

                                            // Create new item if no existing item and quantity > 0
                                            if (existingItemIndex < 0 && (currentBoxQty > 0 || newSingleQty > 0)) {
                                              const orderType = productOrderTypes[product.id] || 'both';
                                              const newItem: OrderItem = {
                                                productId: product.id,
                                                productName: product.name,
                                                sku: product.sku,
                                                quantity: currentBoxQty + newSingleQty,
                                                fulfilledQuantity: currentBoxQty + newSingleQty,
                                                unitPrice: Number(product.unitPrice) || 0,
                                                discount: 0,
                                                tax: Number(product.tax) || 0,
                                                total: Number(product.unitPrice) * (1 + Number(product.tax) / 100),
                                                orderType: orderType,
                                                boxQuantity: currentBoxQty,
                                                singleQuantity: newSingleQty,
                                                fulfilledBoxQuantity: currentBoxQty,
                                                fulfilledSingleQuantity: newSingleQty,
                                              };
                                              const newFormData = { ...formData, items: [...formData.items, newItem] };
                                              setFormData(newFormData);
                                              setHasUnsavedChanges(true);
                                              debouncedAutosave(newFormData);
                                            } else if (existingItemIndex >= 0) {
                                              // Update existing item
                                              const updatedItems = formData.items.map((item, idx) =>
                                                idx === existingItemIndex
                                                  ? {
                                                      ...item,
                                                      singleQuantity: newSingleQty,
                                                      quantity: (item.boxQuantity || 0) + newSingleQty,
                                                      fulfilledQuantity: (item.boxQuantity || 0) + newSingleQty,
                                                      fulfilledSingleQuantity: newSingleQty,
                                                    }
                                                  : item
                                              );
                                              const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                            }
                                          }
                                        }}
                                      >
                                        <AddCircleOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <IconButton 
                                    color="error" 
                                    size="small"
                                    disabled={currentQty === 0}
                                    onClick={() => {
                                      if (currentQty === 1) {
                                        const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      } else if (currentQty > 1) {
                                        const updatedItems = formData.items.map(item => 
                                          item.productId === product.id 
                                            ? { ...item, quantity: item.quantity - 1, fulfilledQuantity: item.quantity - 1 }
                                            : item
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }
                                    }}
                                  >
                                    <RemoveCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={currentQty > 0 ? currentQty : ''}
                                    placeholder="-"
                                    onChange={(e) => {
                                      const newQty = parseInt(e.target.value, 10);
                                      if (isNaN(newQty) || newQty < 0) return;
                                      const orderType = existingItem?.orderType || productOrderTypes[product.id] || 'box';
                                      const stockType = orderType === 'both' ? 'box' : (orderType as 'box' | 'single');
                                      const maxAllowed = getMaxAllowedQuantity(product.id, stockType);
                                      const validQty = Math.min(newQty, maxAllowed);
                                      
                                      if (validQty === 0) {
                                        const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      } else {
                                        // Check if there's an existing item (including ones with 0 quantity after order type switch)
                                        const existingItem = formData.items.find(item => item.productId === product.id);
                                        
                                        if (existingItem) {
                                          // Update existing item
                                          const updatedItems = formData.items.map(item => 
                                            item.productId === product.id 
                                              ? { ...item, quantity: validQty, fulfilledQuantity: validQty }
                                              : item
                                          );
                                          const newFormData = { ...formData, items: updatedItems };
                                          setFormData(newFormData);
                                          setHasUnsavedChanges(true);
                                          debouncedAutosave(newFormData);
                                        } else {
                                          // Create new item only if no existing item
                                          const orderType = productOrderTypes[product.id] || 'box';
                                          const newItem: OrderItem = {
                                            productId: product.id,
                                            productName: product.name,
                                            sku: product.sku,
                                            quantity: orderType === 'both' ? 0 : validQty,
                                            fulfilledQuantity: orderType === 'both' ? 0 : validQty,
                                            unitPrice: Number(product.unitPrice) || 0,
                                            discount: 0,
                                            tax: Number(product.tax) || 0,
                                            total: Number(product.unitPrice) * (1 + Number(product.tax) / 100),
                                            orderType: orderType,
                                            boxQuantity: orderType === 'both' ? 0 : undefined,
                                            singleQuantity: orderType === 'both' ? 0 : undefined,
                                            fulfilledBoxQuantity: orderType === 'both' ? 0 : undefined,
                                            fulfilledSingleQuantity: orderType === 'both' ? 0 : undefined,
                                          };
                                          const newFormData = { ...formData, items: [...formData.items, newItem] };
                                          setFormData(newFormData);
                                          setHasUnsavedChanges(true);
                                          debouncedAutosave(newFormData);
                                        }
                                      }
                                    }}
                                    inputProps={{
                                      min: 0,
                                      max: (() => {
                                        const orderType = existingItem?.orderType || productOrderTypes[product.id] || 'box';
                                        const stockType = orderType === 'both' ? 'box' : (orderType as 'box' | 'single');
                                        return getMaxAllowedQuantity(product.id, stockType);
                                      })(),
                                      style: { textAlign: 'center' }
                                    }}
                                    sx={{ width: '80px', mx: 1 }}
                                    onFocus={(e) => e.target.select()}
                                  />
                                  <IconButton 
                                    color="primary" 
                                    size="small"
                                    disabled={(() => {
                                      const orderType = existingItem?.orderType || productOrderTypes[product.id] || 'box';
                                      const stockType = orderType === 'both' ? 'box' : (orderType as 'box' | 'single');
                                      const maxAllowed = getMaxAllowedQuantity(product.id, stockType);
                                      return currentQty >= maxAllowed;
                                    })()}
                                    onClick={() => {
                                      // Check if there's an existing item with quantity 0 (after order type switch)
                                      const existingItem = formData.items.find(item => item.productId === product.id);
                                      
                                      if (existingItem) {
                                        // Update existing item (including when quantity is 0 after order type switch)
                                        const updatedItems = formData.items.map(item => 
                                          item.productId === product.id 
                                            ? { ...item, quantity: item.quantity + 1, fulfilledQuantity: item.quantity + 1 }
                                            : item
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      } else {
                                        // Create new item only if no existing item
                                        const orderType = productOrderTypes[product.id] || 'box';
                                        const newItem: OrderItem = {
                                          productId: product.id,
                                          productName: product.name,
                                          sku: product.sku,
                                          quantity: orderType === 'both' ? 0 : 1,
                                          fulfilledQuantity: orderType === 'both' ? 0 : 1,
                                          unitPrice: Number(product.unitPrice) || 0,
                                          discount: 0,
                                          tax: Number(product.tax) || 0,
                                          total: Number(product.unitPrice) * (1 + Number(product.tax) / 100),
                                          orderType: orderType,
                                          boxQuantity: orderType === 'both' ? 0 : undefined,
                                          singleQuantity: orderType === 'both' ? 0 : undefined,
                                          fulfilledBoxQuantity: orderType === 'both' ? 0 : undefined,
                                          fulfilledSingleQuantity: orderType === 'both' ? 0 : undefined,
                                        };
                                        const newFormData = { ...formData, items: [...formData.items, newItem] };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }
                                    }}
                                  >
                                    <AddCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}

                {/* Tablet/Mobile Card View */}
                {isTablet && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)'
                      },
                      gap: 2
                    }}
                  >
                    {filteredProducts.map((product) => {
                      const existingItem = formData.items.find(item => item.productId === product.id);
                      const currentQty = existingItem ? existingItem.quantity : 0;
                      
                      return (
                        <Card 
                          key={product.id}
                          sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            border: currentQty > 0 ? 2 : 1,
                            borderColor: currentQty > 0 ? 'primary.main' : 'divider',
                            bgcolor: currentQty > 0 ? 'primary.50' : 'background.paper'
                          }}
                        >
                          <CardMedia
                            component="img"
                            height="120"
                            image={getImageUrl(product.image)}
                            alt={product.name}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = getImageUrl(null);
                            }}
                            onClick={() => handleImagePreview(getImageUrl(product.image), product.name)}
                            sx={{ 
                              objectFit: 'cover',
                              cursor: 'pointer',
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'scale(1.05)',
                              }
                            }}
                          />
                          <CardContent sx={{ flexGrow: 1, p: 2 }}>
                            <Typography variant="subtitle1" component="h3" gutterBottom noWrap>
                              {product.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {product.sku}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {product.unit_size} x {product.qtyInBox} ({product.unitType})
                            </Typography>
                            <Chip 
                              label={product.category?.name || 'Uncategorized'} 
                              size="small" 
                              sx={{ mb: 1 }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Unit: {formatCurrency(existingItem?.unitPrice || product.unitPrice)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Box: {formatCurrency((existingItem?.unitPrice || product.unitPrice) * product.qtyInBox)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                    Stock:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {product.stockCount} (B)
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1, visibility: 'hidden' }}>
                                    Stock:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {product.stockSingle} (S)
                                  </Typography>
                                </Box>
                              </Box>
                              {currentQty > 0 && (
                                <Chip 
                                  label={`Qty: ${currentQty}`} 
                                  color="primary" 
                                  size="small" 
                                />
                              )}
                            </Box>
                            
                            {/* Order Type Selection */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                              <Select
                                size="small"
                                value={currentQty > 0 ? (formData.items.find(item => item.productId === product.id)?.orderType || 'box') : (productOrderTypes[product.id] || 'box')}
                                onChange={async (e) => {
                                  const newOrderType = e.target.value as 'box' | 'single' | 'both';
                                  if (currentQty > 0) {
                                    const existingItem = formData.items.find(item => item.productId === product.id);
                                    const oldOrderType = existingItem?.orderType || 'box';

                                    // DISABLED: Stock restoration is now handled automatically by the backend
                                    // when it detects order type changes during autosave/update
                                    //
                                    // if (oldOrderType !== newOrderType && existingItem && (existingItem.quantity > 0 || (existingItem.boxQuantity || 0) > 0 || (existingItem.singleQuantity || 0) > 0)) {
                                    //   await restoreStockForOrderTypeChange(
                                    //     existingItem.productId,
                                    //     oldOrderType,
                                    //     existingItem.quantity,
                                    //     existingItem.boxQuantity,
                                    //     existingItem.singleQuantity
                                    //   );
                                    // }

                                    const updatedItems = formData.items.map(item => {
                                      if (item.productId === product.id) {
                                        // According to requirements: When switching order types,
                                        // stock is restored and quantities are reset to zero
                                        let newQuantity = 0;
                                        let newFulfilledQuantity = 0;
                                        let newBoxQuantity: number | undefined;
                                        let newSingleQuantity: number | undefined;
                                        let newFulfilledBoxQuantity: number | undefined;
                                        let newFulfilledSingleQuantity: number | undefined;
                                        
                                        if (newOrderType === 'both') {
                                          // Switching TO 'both' type - set both quantities to 0
                                          newBoxQuantity = 0;
                                          newSingleQuantity = 0;
                                          newFulfilledBoxQuantity = 0;
                                          newFulfilledSingleQuantity = 0;
                                          newQuantity = 0;
                                          newFulfilledQuantity = 0;
                                        } else {
                                          // Switching TO 'box' or 'single' type - set main quantity to 0
                                          newQuantity = 0;
                                          newFulfilledQuantity = 0;
                                          // Clear box/single quantities for non-'both' types
                                          newBoxQuantity = undefined;
                                          newSingleQuantity = undefined;
                                          newFulfilledBoxQuantity = undefined;
                                          newFulfilledSingleQuantity = undefined;
                                        }
                                        
                                        return {
                                          ...item, 
                                          orderType: newOrderType,
                                          quantity: newQuantity,
                                          fulfilledQuantity: newFulfilledQuantity,
                                          boxQuantity: newBoxQuantity,
                                          singleQuantity: newSingleQuantity,
                                          fulfilledBoxQuantity: newFulfilledBoxQuantity,
                                          fulfilledSingleQuantity: newFulfilledSingleQuantity
                                        };
                                      }
                                      return item;
                                    });
                                    const newFormData = { ...formData, items: updatedItems };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                  } else {
                                    // For new items (not existing), reset product-level state
                                    setProductOrderTypes({
                                      ...productOrderTypes,
                                      [product.id]: newOrderType
                                    });
                                    // Reset quantities when switching types for new products
                                    setProductBoxQuantities({
                                      ...productBoxQuantities,
                                      [product.id]: 0
                                    });
                                    setProductSingleQuantities({
                                      ...productSingleQuantities,
                                      [product.id]: 0
                                    });
                                  }
                                }}
                                sx={{ minWidth: 100 }}
                              >
                                <MenuItem value="box">Box</MenuItem>
                                <MenuItem value="single">Single</MenuItem>
                                <MenuItem value="both">Both</MenuItem>
                              </Select>
                            </Box>
                            
                            {/* Quantity Controls */}
                            {currentQty > 0 && formData.items.find(item => item.productId === product.id)?.orderType === 'both' ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {/* Box Quantity */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ minWidth: '30px' }}>Box:</Typography>
                                  <IconButton 
                                    color="error" 
                                    size="small"
                                    disabled={((currentQty > 0 && formData.items.find(item => item.productId === product.id)?.boxQuantity) || productBoxQuantities[product.id] || 0) === 0}
                                    onClick={() => {
                                      const currentBoxQty = (currentQty > 0 && formData.items.find(item => item.productId === product.id)?.boxQuantity) || productBoxQuantities[product.id] || 0;
                                      const newBoxQty = Math.max(0, currentBoxQty - 1);
                                      setProductBoxQuantities({
                                        ...productBoxQuantities,
                                        [product.id]: newBoxQty
                                      });
                                      const currentSingleQty = (currentQty > 0 && formData.items.find(item => item.productId === product.id)?.singleQuantity) || productSingleQuantities[product.id] || 0;
                                      const newSingleQty = currentSingleQty;
                                      const newTotalQty = newBoxQty + newSingleQty;
                                      
                                      if (newTotalQty === 0) {
                                        const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      } else {
                                        const updatedItems = formData.items.map(item => 
                                          item.productId === product.id 
                                            ? { 
                                                ...item, 
                                                boxQuantity: newBoxQty,
                                                singleQuantity: newSingleQty,
                                                quantity: newTotalQty,
                                                fulfilledQuantity: newTotalQty,
                                                fulfilledBoxQuantity: newBoxQty,
                                                fulfilledSingleQuantity: newSingleQty
                                              }
                                            : item
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }
                                    }}
                                  >
                                    <RemoveCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={(currentQty > 0 && formData.items.find(item => item.productId === product.id)?.boxQuantity) || productBoxQuantities[product.id] || 0}
                                    onChange={(e) => {
                                      const inputValue = parseInt(e.target.value, 10) || 0;
                                      const maxBoxStock = getMaxAllowedQuantity(product.id, 'box');
                                      const newBoxQty = Math.max(0, Math.min(inputValue, maxBoxStock));
                                      setProductBoxQuantities({
                                        ...productBoxQuantities,
                                        [product.id]: newBoxQty
                                      });
                                      const currentSingleQty = (currentQty > 0 && formData.items.find(item => item.productId === product.id)?.singleQuantity) || productSingleQuantities[product.id] || 0;
                                      const newSingleQty = currentSingleQty;
                                      const newTotalQty = newBoxQty + newSingleQty;
                                      
                                      if (newTotalQty === 0) {
                                        const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      } else {
                                        const updatedItems = formData.items.map(item => 
                                          item.productId === product.id 
                                            ? { 
                                                ...item, 
                                                boxQuantity: newBoxQty,
                                                singleQuantity: newSingleQty,
                                                quantity: newTotalQty,
                                                fulfilledQuantity: newTotalQty,
                                                fulfilledBoxQuantity: newBoxQty,
                                                fulfilledSingleQuantity: newSingleQty
                                              }
                                            : item
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }
                                    }}
                                    inputProps={{
                                      min: 0,
                                      max: product.stockCount || 0,
                                      style: { textAlign: 'center' }
                                    }}
                                    sx={{ width: '60px' }}
                                    onFocus={(e) => e.target.select()}
                                  />
                                  <IconButton 
                                    color="primary" 
                                    size="small"
                                    disabled={((currentQty > 0 && formData.items.find(item => item.productId === product.id)?.boxQuantity) || productBoxQuantities[product.id] || 0) >= getMaxAllowedQuantity(product.id, 'box')}
                                    onClick={() => {
                                      const currentBoxQty = (currentQty > 0 && formData.items.find(item => item.productId === product.id)?.boxQuantity) || productBoxQuantities[product.id] || 0;
                                      const maxBoxStock = getMaxAllowedQuantity(product.id, 'box');
                                      const newBoxQty = Math.min(currentBoxQty + 1, maxBoxStock);
                                      
                                      // Additional safety check
                                      if (newBoxQty > maxBoxStock) return;
                                      setProductBoxQuantities({
                                        ...productBoxQuantities,
                                        [product.id]: newBoxQty
                                      });
                                      const currentSingleQty = (currentQty > 0 && formData.items.find(item => item.productId === product.id)?.singleQuantity) || productSingleQuantities[product.id] || 0;
                                      const newSingleQty = currentSingleQty;
                                      const newTotalQty = newBoxQty + newSingleQty;
                                      
                                      const updatedItems = formData.items.map(item => 
                                        item.productId === product.id 
                                          ? { 
                                              ...item, 
                                              boxQuantity: newBoxQty,
                                              singleQuantity: newSingleQty,
                                              quantity: newTotalQty,
                                              fulfilledQuantity: newTotalQty,
                                              fulfilledBoxQuantity: newBoxQty,
                                              fulfilledSingleQuantity: newSingleQty
                                            }
                                          : item
                                      );
                                      const newFormData = { ...formData, items: updatedItems };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                    }}
                                  >
                                    <AddCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                
                                {/* Single Quantity */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ minWidth: '30px' }}>Single:</Typography>
                                  <IconButton 
                                    color="error" 
                                    size="small"
                                    disabled={((currentQty > 0 && formData.items.find(item => item.productId === product.id)?.singleQuantity) || productSingleQuantities[product.id] || 0) === 0}
                                    onClick={() => {
                                      const currentSingleQty = (currentQty > 0 && formData.items.find(item => item.productId === product.id)?.singleQuantity) || productSingleQuantities[product.id] || 0;
                                      const newSingleQty = Math.max(0, currentSingleQty - 1);
                                      setProductSingleQuantities({
                                        ...productSingleQuantities,
                                        [product.id]: newSingleQty
                                      });
                                      const currentBoxQty = (currentQty > 0 && formData.items.find(item => item.productId === product.id)?.boxQuantity) || productBoxQuantities[product.id] || 0;
                                      const newBoxQty = currentBoxQty;
                                      const newTotalQty = newBoxQty + newSingleQty;
                                      
                                      if (newTotalQty === 0) {
                                        const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      } else {
                                        const updatedItems = formData.items.map(item => 
                                          item.productId === product.id 
                                            ? { 
                                                ...item, 
                                                boxQuantity: newBoxQty,
                                                singleQuantity: newSingleQty,
                                                quantity: newTotalQty,
                                                fulfilledQuantity: newTotalQty,
                                                fulfilledBoxQuantity: newBoxQty,
                                                fulfilledSingleQuantity: newSingleQty
                                              }
                                            : item
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }
                                    }}
                                  >
                                    <RemoveCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={(currentQty > 0 && formData.items.find(item => item.productId === product.id)?.singleQuantity) || productSingleQuantities[product.id] || 0}
                                    onChange={(e) => {
                                      const inputValue = parseInt(e.target.value, 10) || 0;
                                      const maxSingleStock = getMaxAllowedQuantity(product.id, 'single');
                                      const newSingleQty = Math.max(0, Math.min(inputValue, maxSingleStock));
                                      setProductSingleQuantities({
                                        ...productSingleQuantities,
                                        [product.id]: newSingleQty
                                      });
                                      const currentBoxQty = (currentQty > 0 && formData.items.find(item => item.productId === product.id)?.boxQuantity) || productBoxQuantities[product.id] || 0;
                                      const newBoxQty = currentBoxQty;
                                      const newTotalQty = newBoxQty + newSingleQty;
                                      
                                      if (newTotalQty === 0) {
                                        const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      } else {
                                        const updatedItems = formData.items.map(item => 
                                          item.productId === product.id 
                                            ? { 
                                                ...item, 
                                                boxQuantity: newBoxQty,
                                                singleQuantity: newSingleQty,
                                                quantity: newTotalQty,
                                                fulfilledQuantity: newTotalQty,
                                                fulfilledBoxQuantity: newBoxQty,
                                                fulfilledSingleQuantity: newSingleQty
                                              }
                                            : item
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      }
                                    }}
                                    inputProps={{
                                      min: 0,
                                      max: product.stockSingle || 0,
                                      style: { textAlign: 'center' }
                                    }}
                                    sx={{ width: '60px' }}
                                    onFocus={(e) => e.target.select()}
                                  />
                                  <IconButton 
                                    color="primary" 
                                    size="small"
                                    disabled={((currentQty > 0 && formData.items.find(item => item.productId === product.id)?.singleQuantity) || productSingleQuantities[product.id] || 0) >= getMaxAllowedQuantity(product.id, 'single')}
                                    onClick={() => {
                                      const currentSingleQty = (currentQty > 0 && formData.items.find(item => item.productId === product.id)?.singleQuantity) || productSingleQuantities[product.id] || 0;
                                      const maxSingleStock = getMaxAllowedQuantity(product.id, 'single');
                                      const newSingleQty = Math.min(currentSingleQty + 1, maxSingleStock);
                                      
                                      // Additional safety check
                                      if (newSingleQty > maxSingleStock) return;
                                      setProductSingleQuantities({
                                        ...productSingleQuantities,
                                        [product.id]: newSingleQty
                                      });
                                      const currentBoxQty = (currentQty > 0 && formData.items.find(item => item.productId === product.id)?.boxQuantity) || productBoxQuantities[product.id] || 0;
                                      const newBoxQty = currentBoxQty;
                                      const newTotalQty = newBoxQty + newSingleQty;
                                      
                                      const updatedItems = formData.items.map(item => 
                                        item.productId === product.id 
                                          ? { 
                                              ...item, 
                                              boxQuantity: newBoxQty,
                                              singleQuantity: newSingleQty,
                                              quantity: newTotalQty,
                                              fulfilledQuantity: newTotalQty,
                                              fulfilledBoxQuantity: newBoxQty,
                                              fulfilledSingleQuantity: newSingleQty
                                            }
                                          : item
                                      );
                                      const newFormData = { ...formData, items: updatedItems };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                    }}
                                  >
                                    <AddCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <IconButton 
                                  color="error" 
                                  size="small"
                                  disabled={currentQty === 0}
                                  onClick={() => {
                                    if (currentQty === 1) {
                                      const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                      const newFormData = { ...formData, items: updatedItems };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                    } else if (currentQty > 1) {
                                      const updatedItems = formData.items.map(item => 
                                        item.productId === product.id 
                                          ? { ...item, quantity: item.quantity - 1, fulfilledQuantity: item.quantity - 1 }
                                          : item
                                      );
                                      const newFormData = { ...formData, items: updatedItems };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                    }
                                  }}
                                >
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={currentQty > 0 ? currentQty : ''}
                                  placeholder="0"
                                  onChange={(e) => {
                                    const newQty = parseInt(e.target.value, 10);
                                    if (isNaN(newQty) || newQty < 0) return;
                                    const orderType = productOrderTypes[product.id] || 'box';
                                    const stockType = orderType === 'both' ? 'box' : (orderType as 'box' | 'single');
                                    const maxAllowed = getMaxAllowedQuantity(product.id, stockType);
                                    const validQty = Math.min(newQty, maxAllowed);
                                    
                                    if (validQty === 0) {
                                      const updatedItems = formData.items.filter(item => item.productId !== product.id);
                                      const newFormData = { ...formData, items: updatedItems };
                                    setFormData(newFormData);
                                    setHasUnsavedChanges(true);
                                    debouncedAutosave(newFormData);
                                    } else {
                                      // Check if there's an existing item (including ones with 0 quantity after order type switch)
                                      const existingItem = formData.items.find(item => item.productId === product.id);
                                      
                                      if (existingItem) {
                                        // Update existing item
                                        const updatedItems = formData.items.map(item => 
                                          item.productId === product.id 
                                            ? { ...item, quantity: validQty, fulfilledQuantity: validQty }
                                            : item
                                        );
                                        const newFormData = { ...formData, items: updatedItems };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      } else {
                                        // Create new item only if no existing item
                                        const orderType = productOrderTypes[product.id] || 'box';
                                        const newItem: OrderItem = {
                                          productId: product.id,
                                          productName: product.name,
                                          sku: product.sku,
                                          quantity: validQty,
                                          fulfilledQuantity: validQty,
                                          unitPrice: Number(product.unitPrice) || 0,
                                          discount: 0,
                                          tax: Number(product.tax) || 0,
                                          total: Number(product.unitPrice) * (1 + Number(product.tax) / 100),
                                          orderType: orderType,
                                          boxQuantity: orderType === 'both' ? validQty : undefined,
                                          singleQuantity: orderType === 'both' ? 0 : undefined,
                                          fulfilledBoxQuantity: orderType === 'both' ? validQty : undefined,
                                          fulfilledSingleQuantity: orderType === 'both' ? 0 : undefined,
                                        };
                                        const newFormData = { ...formData, items: [...formData.items, newItem] };
                                        setFormData(newFormData);
                                        setHasUnsavedChanges(true);
                                        debouncedAutosave(newFormData);
                                      
                                        if (orderType === 'both') {
                                          setProductBoxQuantities({
                                            ...productBoxQuantities,
                                            [product.id]: validQty
                                          });
                                          setProductSingleQuantities({
                                            ...productSingleQuantities,
                                            [product.id]: 0
                                          });
                                        }
                                      }
                                    }
                                  }}
                                  inputProps={{
                                    min: 0,
                                    max: (() => {
                                      const orderType = productOrderTypes[product.id] || 'box';
                                      const stockType = orderType === 'both' ? 'box' : (orderType as 'box' | 'single');
                                      return getMaxAllowedQuantity(product.id, stockType);
                                    })(),
                                    style: { textAlign: 'center' }
                                  }}
                                  sx={{ width: '80px' }}
                                  onFocus={(e) => e.target.select()}
                                />
                                <IconButton 
                                  color="primary" 
                                  size="small"
                                  disabled={(() => {
                                    const orderType = productOrderTypes[product.id] || 'box';
                                    const stockType = orderType === 'both' ? 'box' : (orderType as 'box' | 'single');
                                    const maxAllowed = getMaxAllowedQuantity(product.id, stockType);
                                    return currentQty >= maxAllowed;
                                  })()}
                                  onClick={() => {
                                    // Check if there's an existing item with quantity 0 (after order type switch)
                                    const existingItem = formData.items.find(item => item.productId === product.id);
                                    
                                    if (existingItem) {
                                      // Update existing item (including when quantity is 0 after order type switch)
                                      const updatedItems = formData.items.map(item => 
                                        item.productId === product.id 
                                          ? { ...item, quantity: item.quantity + 1, fulfilledQuantity: item.quantity + 1 }
                                          : item
                                      );
                                      const newFormData = { ...formData, items: updatedItems };
                                      setFormData(newFormData);
                                      setHasUnsavedChanges(true);
                                      debouncedAutosave(newFormData);
                                    } else {
                                      // Create new item only if no existing item
                                      const orderType = productOrderTypes[product.id] || 'box';
                                      const newItem: OrderItem = {
                                        productId: product.id,
                                        productName: product.name,
                                        sku: product.sku,
                                        quantity: 1,
                                        fulfilledQuantity: 1,
                                        unitPrice: Number(product.unitPrice) || 0,
                                        discount: 0,
                                        tax: Number(product.tax) || 0,
                                        total: Number(product.unitPrice) * (1 + Number(product.tax) / 100),
                                        orderType: orderType,
                                        boxQuantity: orderType === 'both' ? 1 : undefined,
                                        singleQuantity: orderType === 'both' ? 0 : undefined,
                                        fulfilledBoxQuantity: orderType === 'both' ? 1 : undefined,
                                        fulfilledSingleQuantity: orderType === 'both' ? 0 : undefined,
                                      };
                                      const newFormData = { ...formData, items: [...formData.items, newItem] };
                                      setFormData(newFormData);
                                      setHasUnsavedChanges(true);
                                      debouncedAutosave(newFormData);
                                      
                                      if (orderType === 'both') {
                                        setProductBoxQuantities({
                                          ...productBoxQuantities,
                                          [product.id]: 1
                                        });
                                        setProductSingleQuantities({
                                          ...productSingleQuantities,
                                          [product.id]: 0
                                        });
                                      }
                                    }
                                  }}
                                >
                                  <AddCircleOutlineIcon />
                                </IconButton>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialogOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={handleCloseImagePreview}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', p: 2 }}>
            <Typography variant="h6" sx={{ color: 'white', flexGrow: 1 }}>
              {previewImage?.name}
            </Typography>
            <IconButton 
              onClick={handleCloseImagePreview}
              sx={{ color: 'white' }}
              size="large"
            >
              <ClearIcon />
            </IconButton>
          </Box>
          {previewImage && (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.name}
              onError={(e) => {
                (e.target as HTMLImageElement).src = getImageUrl(null);
              }}
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 1,
                cursor: 'pointer'
              }}
              onClick={handleCloseImagePreview}
            />
          )}
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              textAlign: 'center', 
              p: 2,
              fontSize: '0.875rem'
            }}
          >
            Tap image or close button to exit
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRefund ? 'Edit Refund' : 'Add Refund'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Refund Amount"
              type="number"
              fullWidth
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              inputProps={{
                min: 0,
                step: 0.01
              }}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              label="Reason (Optional)"
              fullWidth
              multiline
              rows={3}
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Enter reason for refund..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveRefund}
            disabled={!refundAmount || parseFloat(refundAmount) <= 0}
          >
            {editingRefund ? 'Update' : 'Add'} Refund
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning Dialog */}
      <Dialog
        open={warningDialogOpen}
        onClose={() => setWarningDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Alert severity="warning" sx={{ display: 'flex', alignItems: 'center', width: '100%', p: 0, pl: 2 }}>
            Warning
          </Alert>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ pt: 2 }}>
            Please select a customer before adding products to the order.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setWarningDialogOpen(false)} 
            variant="contained" 
            color="warning"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderForm;
